import React, {
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import { useLocation } from "react-router-dom";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import imagecustomRounded from "assets/images/imagecustomrounded.png";
import { getAuthCookie } from "../utils/authCookies";
import {
  buildInstallationOrderInvoicePdfAttachment,
  buildRemovalOrderInvoicePdfAttachment,
  buildSalesOrderInvoicePdfAttachment,
} from "utils/installationOrderPdfAttachment";
import { INVOICE_STATUS100_SHELL_STYLES } from "./invoiceStatus100ShellStyles";
import {
  type InvoiceType,
  computeInstallationRemovalTaxAmount,
  computeInvoiceJobSubtotal,
  computeInvoiceNotesDisplay,
  computeJobProductSubtotalFromItems,
  computeJobProductTaxFromItems,
  computeSalesSubtotalFromItems,
  computeSalesTaxAmount,
  computeInstallationRemovalDiscountAmount,
  resolveInstallationRemovalDiscountCode,
  getSalesOrderLineDiscount,
  getSalesOrderLineTotalAmount,
  getSalesOrderLineUnitRetailPrice,
  computeShippingAmountFromOrder,
  computeSpecialFeesFromOrder,
  deliveryAddressLabelForOrder,
  normalizeInvoiceTypeFromOrder,
  getGateAccessCodeForJobInvoice,
  formatInvoiceDisplayNumber,
  getInstallationAddressForInvoice,
  getInstallerNameForJobInvoice,
  getJobScheduledDate,
  getRemovalAddressForInvoice,
  invoiceTitleWords,
  jobDetailsSectionTitle,
  serviceCostLineLabel,
} from "./invoiceDocument/index";
import {
  formatShippingChargeOrderTotalsLabel,
  formatSpecialFeeOrderTotalsLabel,
} from "utils/orderTotalsChargeLabels";
import CompactApprovedProofCard from "components/CompactApprovedProofCard";
import {
  fetchProjectApprovedMessage,
  resolveApprovedProofVersionSummary,
} from "components/proejctsandquoteapicalls";
import { isSignagePortalRoute } from "utils/isSignagePortalRoute";
import { downloadWorkDriveFile } from "api/realesyStudioApi";
import { parseCustomInformation } from "components/SalesOrderDetails/salesOrderDetailsTaxAndUiHelpers";
import { DiscountAmountDisplay } from "components/DiscountLabelBadge";
import PublicOrderPayUi from "components/PublicOrderPayUi";
import {
  fetchNormalizedPublicOrderForSummary,
  pickLatestOrderPaymentInfo,
  displayPaymentPaidByName,
  publicOrderNormalizeKindFromFlags,
  sumOrderPaymentInfoAmountPaid,
} from "components/orderSummary";

const cleanInvoiceQueryParam = (value?: string | null) =>
  String(value || "")
    .trim()
    .replace(/^"+|"+$/g, "")
    .replace(/^'+|'+$/g, "");

export type { InvoiceType } from "./invoiceDocument/index";

export type ServiceInfo = {
  companyName: string;
  contactLabel?: string;
  contactPhone?: string;
  addressLine?: string;
  email?: string;
  website?: string;
  termsCancellation?: string;
  termsService?: string;
};

/** Imperative handle: open HTML preview / browser print only (no client-side PDF generation). */
export type InvoiceDocHandle = {
  preview: () => void;
  print: () => void;
};

type InvoiceTemplate = {
  template_name: string;
  company_logo?: { url?: string };   // legacy
  images?: { url?: string };         // your schema
  company_name?: string;
  address?: string;
  phone_number?: string;
  email?: string;
  website?: string;
  custom_message?: string;
  job_types?: string[];              // e.g. ["installation","orders","removal"]
  default?: boolean;                 // legacy default flag
  default_for?: string[];            // your schema (e.g. ["orders"])
};

type CustomField = {
  _id?: string;
  field_name?: string;
  field_type?: "text" | "file" | string;
  value?: any;
  required?: boolean;
};

type Props = {
  type?: InvoiceType;
  /** Pass the full order object (sales / installation / removal). */
  order?: any;
  /** Optional map from image ids to URLs, used for sales item thumbnails. */
  imageUrlMap?: Record<string, string>;
  /** Optional resolver to turn an id/path into a URL (e.g. signed URL). */
  resolveImageSrc?: (idOrUrl?: string) => string;
  /** Optional branding logo URL. */
  logoUrl?: string;
  /** Optional provider data; if not given, sensible defaults are used per type. */
  serviceInfo?: Partial<ServiceInfo>;
};

/* -------------------------- helpers & constants -------------------------- */

const show = (v: any) =>
  v === undefined || v === null || v === "" ? "---" : v;

const dateOnly = (d?: string) =>
  d && d !== "0001-01-01T00:00:00Z"
    ? new Date(d).toISOString().slice(0, 10)
    : "---";

const money = (n: any) => `$${(Number(n) || 0).toFixed(2)}`;

const showMoney = (v: any) =>
  v === undefined || v === null || Number.isNaN(Number(v)) ? "---" : money(Number(v));

// map numeric status → human text for install / removal
const statusText = (order: any, type: InvoiceType) => {
  if (typeof order?.status === "number") {
    const map: Record<number, string> = {
      200: "Pending",
      300: "Scheduled",
      400: "In Progress",
      500: "Completed",
    };
    return map[order.status] ?? String(order.status);
  }
  return (
    order?.status_label ||
    order?.status_text ||
    order?.status ||
    (type === "sales" ? "Confirmed" : "Scheduled")
  );
};

const priorityText = (p: any) => {
  const n = Number(p);
  if (n === 1) return "High";
  if (n === 2) return "Medium";
  if (n === 3) return "Low";
  return show(p);
};

const is24Hex = (value?: string) => !!value && /^[0-9a-f]{24}$/i.test(value);

/** Display empty values like Order_summary (`—`). */
const osShow = (value?: string | number | null) =>
  value === undefined || value === null || value === "" ? "—" : String(value);

/** Normalize Mongo-shaped ids and plain strings from order payloads. */
const normalizeOrderUserId = (v: unknown): string => {
  if (v == null || v === "") return "";
  if (typeof v === "object" && v !== null && "$oid" in (v as Record<string, unknown>)) {
    return String((v as { $oid?: string }).$oid ?? "").trim();
  }
  if (typeof v === "object" && v !== null && "id" in (v as Record<string, unknown>)) {
    return String((v as { id?: unknown }).id ?? "").trim();
  }
  const s = String(v).trim();
  return s === "[object Object]" ? "" : s;
};

const attachmentMapToBlob = (map?: Record<string, string>): { name: string; blob: Blob } | null => {
  if (!map) return null;
  const entries = Object.entries(map);
  if (!entries.length) return null;
  const [name, base64] = entries[0];
  if (!base64) return null;
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) arr[i] = bytes.charCodeAt(i);
  return { name, blob: new Blob([arr], { type: "application/pdf" }) };
};

const sectionIconBox: React.CSSProperties = {
  width: 26,
  height: 26,
  minWidth: 26,
  borderRadius: 6,
  border: "1px solid #d8dde6",
  background: "#f3f4f6",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const sectionTitle: React.CSSProperties = {
  fontSize: "0.975rem",
  color: "#111827",
  letterSpacing: "0.01em",
  fontFamily: "Roboto, sans-serif",
  fontWeight: 700,
};

const fieldLabel: React.CSSProperties = {
  marginBottom: 6,
  fontSize: "0.875rem",
  color: "#111827",
  letterSpacing: "0.01em",
  fontFamily: "Roboto, sans-serif",
  fontWeight: 500,
};

const fieldValue: React.CSSProperties = {
  lineHeight: 1.5,
  fontSize: "0.875rem",
  color: "#495363",
  letterSpacing: "0.01em",
  fontFamily: "Roboto, sans-serif",
  fontWeight: 500,
  minWidth: 0,
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

/** Parse order/API dates: ISO string, epoch, `Date`, or Mongo `{ $date }`. */
function parseOrderDateValue(v: unknown): Date | null {
  if (v == null || v === "") return null;
  if (v instanceof Date && !Number.isNaN(v.getTime())) return v;
  if (typeof v === "object" && v !== null && "$date" in (v as Record<string, unknown>)) {
    return parseOrderDateValue((v as { $date?: unknown }).$date);
  }
  if (typeof v === "string" || typeof v === "number") {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function formatMdY(d: Date | null | undefined): string {
  if (!d || Number.isNaN(d.getTime())) return "—";
  return `${d.getMonth() + 1}-${d.getDate()}-${d.getFullYear()}`;
}

function formatInvoiceAddressLines(address?: any): string[] {
  if (!address) return ["—"];
  const ad = address?.address_details ?? address ?? {};
  const name =
    ad?.name ||
    address?.label ||
    (address?.first_name && `${address.first_name} ${address.last_name}`);
  const phone = ad?.phone || address?.phone;
  const lines: string[] = [];
  // if (name) lines.push(String(name));
  // if (phone) lines.push(String(phone));
  const street = [ad?.addressLine1, ad?.addressLine2].filter(Boolean);
  if (street.length) lines.push(street.join(" "));
  const cityLine = [ad?.city, ad?.state, ad?.zip].filter(Boolean).join(", ");
  if (cityLine) lines.push(cityLine);
  if (ad?.country) lines.push(String(ad.country));
  return lines.length ? lines : ["—"];
}


async function fetchInvoiceTemplates(signageId: string, token?: string): Promise<InvoiceTemplate[]> {
  const sid = String(signageId || "").trim();
  if (!sid) return [];
  const res = await fetch(
    `${process.env.REACT_APP_API_URL}/invoice-templates/signage/${encodeURIComponent(sid)}/default`,
    {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    }
  );
  if (!res.ok) throw new Error(`Failed to load invoice templates (${res.status})`);
  const payload = await res.json();
  if (Array.isArray(payload)) return payload;
  if (payload?.data && !Array.isArray(payload.data)) return [payload.data];
  if (payload?.data && Array.isArray(payload.data)) return payload.data;
  if (payload && typeof payload === "object") return [payload];
  return [];
}

function LineItemAttributes({ item }: { item: any }) {
  const attrs =
    item?.attributes && typeof item.attributes === "object"
      ? item.attributes
      : {};
  const entries = Object.entries(attrs);
  if (!entries.length) return null;
  return (
    <div className="small text-muted pdf-safe mt-1">
      {entries.map(([k, v], i) => (
        <div key={i}>
          <strong className="text-secondary">
            {String(k).replace(/^./, (c) => c.toUpperCase())}:
          </strong>{" "}
          {String(v)}
        </div>
      ))}
    </div>
  );
}

const ImageThumb = ({
  src,
  alt,
}: {
  src: string;
  alt: string;
}) => {
  const [ok, setOk] = React.useState<boolean>(false);

  return (
    <div
      className="d-inline-block align-middle"
      style={{ borderRadius: 8, overflow: "hidden" }}
    >
      {/* Primary: real <img> */}
      <img
        src={src}
        alt={alt}
        className="mini-thumb"
        loading="eager"
        decoding="async"
        // NOTE: remove crossOrigin/referrerPolicy just in case CSS/CSP is interfering
        style={{ width: 64, height: 64, objectFit: "cover", display: ok ? "block" : "none" }}
        onLoad={(e) => {
          const img = e.currentTarget;
          setOk(true);
        }}
        onError={(e) => {
          console.warn("Customization image failed to load → fallback div", { src });
          setOk(false);
        }}
      />

      {/* Fallback: background-image div (renders even if <img> is hidden by some CSS) */}
      {!ok && (
        <div
          aria-label={alt}
          style={{
            width: 64,
            height: 64,
            backgroundImage: `url("${src}")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
        />
      )}
    </div>
  );
};

/** Sales line thumb — matches Order_summary `OrderSummaryLineItemThumb` (80×80, contain). */
const InvoiceStatus100Thumb: React.FC<{
  imageUrl?: string;
  imageAlt?: string;
  productName?: string;
  pdfFileId?: string;
}> = ({ imageUrl, imageAlt, productName, pdfFileId }) => {
  const src = String(imageUrl || "").trim();
  const productImageSrc = src || imagecustomRounded;

  return (
    <div
      style={{
        width: 80,
        height: 80,
        minWidth: 80,
        minHeight: 80,
        borderRadius: 8,
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      <img
        className="status100-thumb"
        src={productImageSrc}
        data-pdf-file-id={pdfFileId || undefined}
        alt={imageAlt || productName || "Product"}
        loading="eager"
        decoding="async"
        style={{ width: 80, height: 80, objectFit: "contain", display: "block" }}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).src = imagecustomRounded;
        }}
      />
    </div>
  );
};

// ⛔️ remove `export default` here
function LineItemCustomizations({
  item,
  token,
  imageUrlMap,
  resolveImageSrc,             // ✅ accept resolver from parent
}: {
  item: any;
  token?: string;
  imageUrlMap?: Record<string, string>;
  resolveImageSrc?: (idOrUrl?: string) => string;
}) {
  const fields: CustomField[] = Array.isArray(item?.customizations?.fields)
    ? item.customizations.fields
    : [];
  if (!fields.length) return null;

  const isFileField = (f: CustomField) => String(f?.field_type || "").toLowerCase() === "file";

  // Keep THIS normalize, delete the top-level duplicate
  const normalizeFileValues = (val: any): string[] => {
    if (!val) return [];
    if (Array.isArray(val) && val.every(v => typeof v === "string")) {
      return val.filter(Boolean).map(String);
    }
    if (Array.isArray(val) && val.every(v => v && typeof v === "object")) {
      return val.map(v => (v?.url ? String(v.url).trim() : "")).filter(Boolean);
    }
    if (val && typeof val === "object" && val.url) {
      return [String(val.url).trim()];
    }
    if (typeof val === "string") {
      return val.split(",").map(s => s.trim()).filter(Boolean);
    }
    return [];
  };

  // id/url -> usable URL in THIS file
  // id/url -> usable URL (prefer resolver IF it returns a value; else fall back to S3 helper)
  // id/url -> usable URL (prefer map; fallback to helper with map)
  const toUrl = (raw?: string) => {
    if (!raw) return "";
    const v = String(raw).trim();

    // pass through full URLs / data URIs
    if (/^https?:\/\//i.test(v) || /^data:/i.test(v)) return v;

    // 1) from imageUrlMap (this should be your mapFromHook1 or merged map)
    const fromMap = imageUrlMap?.[v];
    if (fromMap) return fromMap;

    // 2) final fallback: use your helper with the MAP (not headers)
    //    getImageSrc(signature): (raw: string, map?: Record<string,string>) => string | undefined
    return (getImageSrc(v, imageUrlMap || {}) as string | undefined) || "";
  };
  // does any field have a value?
  const fieldHasValue = (f: CustomField) => {
    if (isFileField(f)) {
      return normalizeFileValues(f?.value).length > 0;
    }
    const v = f?.value;
    if (v === undefined || v === null) return false;
    return String(v).trim().length > 0;
  };

  const textFields = fields.filter((f) => !isFileField(f) && fieldHasValue(f));
  const fileFields = fields.filter((f) => isFileField(f) && fieldHasValue(f));

  if (!textFields.length && !fileFields.length) {
    return null;
  }

  return (
    <div className="mt-2">
      {/* TEXT FIELDS (non-empty only; same order as API) */}
      {textFields.length > 0 && (
        <div className="mb-2">
          {textFields.map((f, i) => (
            <div key={`txt-${f?._id || f?.field_name || i}`} className="mb-1">
              <strong className="me-2 meta-value">
                {String(f?.field_name || "").trim() || "Field"}:
              </strong>
              <span className="meta-value">{String(f?.value ?? "").trim()}</span>
            </div>
          ))}
        </div>
      )}
      {/* FILE / IMAGE FIELDS (always after text) */}
      {fileFields.length > 0 && (
        <div className="mt-2">
          {fileFields.map((f, i) => {
            const vals = normalizeFileValues(f?.value);
            const label = String(f?.field_name || "").trim() || "Image";
            return (
              <div key={`file-${f?._id || label || i}`} className="mb-2">
                <div className="mb-1">
                  <strong className="me-2">{label}:</strong>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {vals.map((one, j) => {
                    const src = toUrl(one);
                    if (src) {
                      return (
                        <a
                          key={`file-${i}-${j}`}
                          href={src}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="d-inline-block align-middle"
                          style={{ borderRadius: 8, overflow: "hidden" }}
                        >
                          <ImageThumb src={src} alt={label || `image-${j}`} />
                        </a>
                      );
                    }
                    if (/^[0-9a-f]{24}$/i.test(String(one || "").trim())) {
                      return (
                        <span key={`file-${i}-${j}`} className="meta-value" style={{ alignSelf: "center" }}>
                          Loading…
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LineItemCustomInformation({ item }: { item: any }) {
  if (String(item?.inventory_source || "").toLowerCase() !== "custom") return null;

  const raw = String(item?.custom_information ?? "").trim();
  if (!raw) return null;

  const pairs = parseCustomInformation(raw);
  const rows = pairs.length > 0 ? pairs : [{ key: "Details", value: raw }];

  return (
    <div className="mt-2">
      {rows.map(({ key, value }, idx) => (
        <div key={`ci-${key || idx}`} className="mb-1">
          <strong className="me-2 meta-value">{key || "Field"}:</strong>
          <span className="meta-value">{value}</span>
        </div>
      ))}
    </div>
  );
}

/* ---------------------------- Main component ----------------------------- */

const InvoiceDocument = forwardRef<InvoiceDocHandle, Props>(
  (
    {
      type: pType,
      order: pOrder,
      imageUrlMap: pImageUrlMap,
      resolveImageSrc: pResolveImageSrc,
      logoUrl: pLogoUrl,
      serviceInfo: pServiceInfo,
    },
    ref
  ) => {
    // ✅ Merge props with route state (without redeclaring the same identifiers)
    const loc = useLocation() as any;
    const state = (loc?.state ?? {}) as Partial<Props> & { autoPrint?: boolean };
    const query = useMemo(() => new URLSearchParams(String(loc?.search || "")), [loc?.search]);

    const invoiceTypeFromPath = useMemo((): InvoiceType | null => {
      const p = String(loc?.pathname || "").toLowerCase();
      if (p.includes("/installationorder/invoice")) return "installation";
      if (p.includes("/removaljob/invoice")) return "removal";
      if (p.includes("/salesorder/invoice")) return "sales";
      return null;
    }, [loc?.pathname]);

    const effType: InvoiceType =
      pType ??
      (state.type as InvoiceType) ??
      invoiceTypeFromPath ??
      normalizeInvoiceTypeFromOrder(pOrder ?? state.order) ??
      "sales";

    const queryAccessToken = useMemo(
      () => cleanInvoiceQueryParam(query.get("access_token") || query.get("token")),
      [query]
    );
    const queryOrderId = useMemo(
      () => cleanInvoiceQueryParam(query.get("order_id") || query.get("project_id")),
      [query]
    );
    const queryProjectId = useMemo(
      () => cleanInvoiceQueryParam(query.get("project_id")),
      [query]
    );
    const queryPublicUrl = useMemo(
      () => cleanInvoiceQueryParam(query.get("public_url") || query.get("Public_url")),
      [query]
    );

    const parsedPublicProjectId = useMemo(() => {
      if (!queryPublicUrl) return "";
      try {
        const parsed = queryPublicUrl.startsWith("http")
          ? new URL(queryPublicUrl)
          : new URL(queryPublicUrl, window.location.origin);
        const m = parsed.pathname.match(/\/public\/projects\/([^/?#]+)/i);
        return cleanInvoiceQueryParam(m?.[1] || "");
      } catch {
        return "";
      }
    }, [queryPublicUrl]);

    const parsedPublicTokenFromUrl = useMemo(() => {
      if (!queryPublicUrl) return "";
      try {
        const parsed = queryPublicUrl.startsWith("http")
          ? new URL(queryPublicUrl)
          : new URL(queryPublicUrl, window.location.origin);
        return cleanInvoiceQueryParam(
          parsed.searchParams.get("access_token") || parsed.searchParams.get("token")
        );
      } catch {
        return "";
      }
    }, [queryPublicUrl]);

    const effectiveProjectId = queryProjectId || parsedPublicProjectId;
    const effectiveAccessToken = queryAccessToken || parsedPublicTokenFromUrl;

    const isPublicInvoiceView = Boolean(effectiveAccessToken && queryOrderId);
    const pathnameLower = String(loc?.pathname || "").toLowerCase();
    const isPublicSalesInvoiceRoute =
      invoiceTypeFromPath === "sales" || pathnameLower.includes("/salesorder/invoice");
    const isPublicInstallationInvoiceRoute =
      invoiceTypeFromPath === "installation" || pathnameLower.includes("/installationorder/invoice");
    const isPublicRemovalInvoiceRoute =
      invoiceTypeFromPath === "removal" || pathnameLower.includes("/removaljob/invoice");
    /** Customer invoice link from email (sales / installation / removal). */
    const isPublicCustomerInvoiceOpen =
      Boolean(queryOrderId) &&
      (isPublicSalesInvoiceRoute || isPublicInstallationInvoiceRoute || isPublicRemovalInvoiceRoute);
    const isPrivateInvoicePreview = Boolean(
      (pOrder ?? state.order) && !isPublicInvoiceView && !isPublicCustomerInvoiceOpen
    );
    const showInvoiceActionBar = !isPublicCustomerInvoiceOpen && !isPublicInvoiceView;

    const invoiceAuthToken = useMemo(
      () => String(getAuthCookie("sessionToken") || effectiveAccessToken || "").trim(),
      [effectiveAccessToken]
    );

    const [invoicePublicOrder, setInvoicePublicOrder] = useState<any>(null);
    const [publicInvoiceOrderLoading, setPublicInvoiceOrderLoading] = useState(false);
    const baseEffOrder: any = useMemo(
      () => pOrder ?? state.order ?? {},
      [pOrder, state.order]
    );
    /** Prefer API-fetched order whenever set (public link or fetch while logged in). */
    const effOrder: any = useMemo(
      () => invoicePublicOrder ?? baseEffOrder,
      [invoicePublicOrder, baseEffOrder]
    );

    const effLogoUrl = pLogoUrl ?? state.logoUrl;
    const effImageUrlMap = pImageUrlMap ?? state.imageUrlMap;
    const effResolveImageSrc = pResolveImageSrc ?? state.resolveImageSrc;
    const [tpls, setTpls] = useState<InvoiceTemplate[] | null>(null);
    const [tplErr, setTplErr] = useState<string | null>(null);
    const [downloadingPdf, setDownloadingPdf] = useState(false);

    const isSales = effType === "sales";
    const isInstall = effType === "installation";
    const isRemoval = effType === "removal";

    useEffect(() => {
      if (!queryOrderId) return;
      if (isPrivateInvoicePreview) return;
      if (!isSales && !isInstall && !isRemoval) return;
      let mounted = true;
      setPublicInvoiceOrderLoading(true);
      (async () => {
        try {
          const normalized = await fetchNormalizedPublicOrderForSummary(
            isRemoval,
            isInstall,
            effectiveProjectId,
            queryOrderId,
            effectiveAccessToken
          );
          if (mounted) setInvoicePublicOrder(normalized);
        } catch {
          if (mounted) setInvoicePublicOrder(null);
        } finally {
          if (mounted) setPublicInvoiceOrderLoading(false);
        }
      })();
      return () => {
        mounted = false;
      };
    }, [
      isSales,
      isInstall,
      isRemoval,
      isPrivateInvoicePreview,
      effectiveProjectId,
      queryOrderId,
      effectiveAccessToken,
    ]);

    // Decide whose templates to fetch (usually signage company on the order)
    const ownerUserId =
      String(
        effOrder?.signage_id ||
        effOrder?.signage?._id ||
        effOrder?.signage?.id ||
        effOrder?.user_id ||
        ""
      ).trim();

    const [templates, setTemplates] = React.useState<InvoiceTemplate[] | null>(null);
    const [hasApprovedMessage, setHasApprovedMessage] = useState(false);
    const [approvedMessageData, setApprovedMessageData] = useState<any>(null);

    React.useEffect(() => {
      (async () => {
        try {
          if (!ownerUserId) { setTemplates([]); return; }
          const data = await fetchInvoiceTemplates(ownerUserId, invoiceAuthToken);
          setTemplates(Array.isArray(data) ? data : []);
          setTplErr(null);
        } catch (e: any) {
          console.error("Templates fetch failed:", e);
          setTemplates([]);
          setTplErr(e?.message || "Failed to fetch templates");
        }
      })();
    }, [ownerUserId, invoiceAuthToken]);

    const linkedProjectId = String(
      effOrder?.project_id ||
      effOrder?.project?.id ||
      effOrder?.project?._id ||
      effOrder?.linked_project_id ||
      ""
    ).trim();
    React.useEffect(() => {
      let cancelled = false;
      if (!invoiceAuthToken || !linkedProjectId) {
        setHasApprovedMessage(false);
        setApprovedMessageData(null);
        return;
      }
      (async () => {
        try {
          const approved = await fetchProjectApprovedMessage(invoiceAuthToken, linkedProjectId);
          if (cancelled) return;
          const hasMessage = Boolean(approved?.has_approved_message);
          setHasApprovedMessage(hasMessage);
          setApprovedMessageData(hasMessage ? approved?.data ?? null : null);
        } catch {
          if (cancelled) return;
          setHasApprovedMessage(false);
          setApprovedMessageData(null);
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [linkedProjectId, invoiceAuthToken]);
    // `/invoice-templates/default` already returns the effective default template.
    const selectedTpl: InvoiceTemplate | null = React.useMemo(() => {
      if (!Array.isArray(templates)) return null;
      return templates[0] ?? null;
    }, [templates]);

    // after you compute `selectedTpl`
    const tplLogoRaw =
      String(
        selectedTpl?.images?.url ||
        selectedTpl?.company_logo?.url ||
        ""
      ).trim();

    const logoIds: string[] = React.useMemo(() => {
      // only 24-hex IDs need presigning
      return /^[0-9a-f]{24}$/i.test(tplLogoRaw) ? [tplLogoRaw] : [];
    }, [tplLogoRaw]);

    // If you already have `syntheticRowsForCustomizations`, extend it to include the logo id:
    const syntheticRows = React.useMemo(() => {
      const ids = logoIds;
      return ids.length ? [{ images: ids.map((id) => ({ url: id })) }] : [];
    }, [logoIds]);

    // Build the map including logo + custom image ids
    const mapFromHook2 =
      useVariantImageUrlMap(syntheticRows, invoiceAuthToken) ?? {};
    const effTplLogoUrl2 = React.useMemo(() => {
      const v = tplLogoRaw;
      if (!v) return "";
      if (/^(https?:|data:|blob:)/i.test(v)) return v;              // already a URL
      const fromMap = mapFromHook2[v];
      if (fromMap) return fromMap;
      return (getImageSrc(v, mapFromHook2 || {}) as string) || "";  // final fallback
    }, [tplLogoRaw, mapFromHook2]);



    const effServiceInfo: ServiceInfo = {
      companyName: selectedTpl?.company_name || "",
      contactLabel: "Contact",
      contactPhone: selectedTpl?.phone_number || "",
      addressLine: selectedTpl?.address || "",
      email: selectedTpl?.email || "",
      website: selectedTpl?.website || "",
      termsCancellation: "",                     // no field in schema → keep empty
      termsService: selectedTpl?.custom_message || "",
    };


    const printRootRef = useRef<HTMLDivElement>(null);
    /** Refreshed on browser print / PDF capture so header matches preview + print. */
    const [printedAt, setPrintedAt] = useState(() => new Date());
    useEffect(() => {
      const onBeforePrint = () => setPrintedAt(new Date());
      window.addEventListener("beforeprint", onBeforePrint);
      return () => window.removeEventListener("beforeprint", onBeforePrint);
    }, []);

    /* ---------- field mapping across the three schemas ---------- */
    const titleWords = invoiceTitleWords(isSales, isInstall);

    // contact block
    const salesCustomerFullName = [
      String(effOrder?.customer_first_name ?? "").trim(),
      String(effOrder?.customer_last_name ?? "").trim(),
    ]
      .filter(Boolean)
      .join(" ");

    const salesAgentId = normalizeOrderUserId(effOrder?.agent_id);
    const salesTeamMemberId = normalizeOrderUserId(effOrder?.team_member_id);
    const salesAgentOrTeamMemberLabel =
      salesAgentId && salesTeamMemberId
        ? "Agent Name"
        : salesTeamMemberId && !salesAgentId
          ? "Team Member Name"
          : "Agent Name";

    const email = isSales
      ? effOrder?.customer_email || effOrder?.email
      : String(effOrder?.user_email ?? "").trim();

    const phone = isSales
      ? String(effOrder?.customer_phone ?? "").trim()
      : String(effOrder?.user_phone ?? "").trim();

    const brokerage = String(effOrder?.brokerage_firm_name ?? "").trim();

    const office = String(effOrder?.office?.officeName ?? "").trim();
    const agent = isSales
      ? salesCustomerFullName
      : String(effOrder?.user_name ?? "").trim();

    // addresses
    const billing = effOrder?.billing_address || effOrder?.billingAddress;
    const shipping = effOrder?.shipping_address || effOrder?.shippingAddress;

    const installAddress = getInstallationAddressForInvoice(effOrder, shipping);
    const removalAddress = getRemovalAddressForInvoice(effOrder, shipping);

    const scheduled = getJobScheduledDate(isInstall, isRemoval, effOrder);

    // items
    const items: any[] = isSales
      ? Array.isArray(effOrder?.items) ? effOrder.items : []
      : Array.isArray(effOrder?.line_items) ? effOrder.line_items : [];

    /** Include preview_url / image_url in synthetic images so useVariantImageUrlMap presigns them (matches Order Summary). */
    const lineItemsForImageMap = React.useMemo(
      () =>
        items.map((item: any) => {
          const images = Array.isArray(item?.images) ? [...item.images] : [];
          const previewId = String(item?.preview_url || "").trim();
          const imageId = String(item?.image_url || "").trim();
          if (previewId) images.push({ url: previewId });
          if (imageId) images.push({ url: imageId });
          return { ...item, images };
        }),
      [items]
    );

    // Collect 24-hex IDs from customizations → make synthetic rows so the hook presigns them
    const customizationIds: string[] = React.useMemo(() => {
      const out: string[] = [];
      for (const it of items) {
        const fields = Array.isArray(it?.customizations?.fields) ? it.customizations.fields : [];
        for (const f of fields) {
          if (String(f?.field_type || "").toLowerCase() !== "file") continue;
          const raw = f?.value;
          const id =
            typeof raw === "string" ? raw.trim()
              : (raw && typeof raw === "object" && raw.url ? String(raw.url).trim() : "");
          if (/^[0-9a-f]{24}$/i.test(id)) out.push(id);
        }
      }
      return Array.from(new Set(out));
    }, [items]);
    const syntheticRowsForCustomizations = React.useMemo(
      () => (customizationIds.length ? [{ images: customizationIds.map(id => ({ url: id })) }] : []),
      [customizationIds]
    );
    // Build/choose the map (props wins; else hook) – include synthetic rows
    const mapFromHook1 = useVariantImageUrlMap(
      syntheticRowsForCustomizations,
      invoiceAuthToken
    ) ?? {};


    const itemQty = (it: any) => {
      // For rotational products, show rotational_allocated or serial_allocations length
      if (it?.is_rotational || it?.inventory_source === "rotational") {
        return it?.rotational_allocated ??
          (Array.isArray(it?.serial_allocations) ? it.serial_allocations.length : 0);
      }
      // For normal products
      return it?.quantity ??
        it?.quantity_to_be_removed ??
        1;
    };
    const approvedProofCards = useMemo(() => {
      const approvedRoot = (approvedMessageData as any) || {};
      const approvedFiles = Array.isArray(approvedRoot?.approved_files) ? approvedRoot.approved_files : [];
      if (approvedFiles.length === 0) return [];

      const grouped = new Map<number, any[]>();
      approvedFiles.forEach((f: any) => {
        const version = Number(f?.version);
        const key = Number.isFinite(version) && version > 0 ? Math.floor(version) : 1;
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key)!.push(f);
      });

      return Array.from(grouped.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([version, files]) => {
          const first = files[0] || {};
          const compactProofFiles = files.map((f: any, idx: number) => ({
            id: String(f?.id ?? f?._id ?? idx),
            name: String(f?.original_name ?? f?.file_name ?? f?.name ?? "File"),
            href:
              String(f?.preview_url ?? "").trim() ||
              String(f?.file_url ?? f?.url ?? "").trim(),
            previewUrl: String(f?.preview_url ?? f?.file_url ?? f?.url ?? "").trim(),
            fileId: String(f?.file_id ?? "").trim(),
            quantity: Number.isFinite(Number(f?.quantity)) ? Number(f?.quantity) : undefined,
            sizeLabel: Number.isFinite(Number(f?.file_size)) ? `${Math.round(Number(f?.file_size) / 1024)} KB` : "—",
          }));
          const approvedByName = String(
            first?.design_approved_by?.name ??
            first?.updated_by?.name ??
            (approvedRoot as any)?.design_approved_by?.name ??
            (approvedRoot as any)?.approved_by_name ??
            effOrder?.design_approved_by?.name ??
            effOrder?.project?.design_approved_by?.name ??
            ""
          ).trim();
          const approvedAtRaw =
            first?.design_approved_at ??
            first?.updated_at ??
            (approvedRoot as any)?.design_approved_at ??
            (approvedRoot as any)?.approved_at ??
            effOrder?.design_approved_at ??
            effOrder?.project?.design_approved_at;
          const approvedAtLabel = approvedAtRaw ? new Date(approvedAtRaw).toLocaleString() : "";
          const shippingName = String(
            first?.shipping_method?.name ??
            (approvedRoot as any)?.shipping_method?.name ??
            effOrder?.shipping_method?.name ??
            ""
          ).trim();
          const shippingDesc = String(
            first?.shipping_method?.description ??
            (approvedRoot as any)?.shipping_method?.description ??
            effOrder?.shipping_method?.description ??
            ""
          ).trim();
          const shippingAmountRaw = first?.shipping_amount ?? (approvedRoot as any)?.shipping_amount;
          const shippingAmountLabel = Number.isFinite(Number(shippingAmountRaw))
            ? `$${Number(shippingAmountRaw).toFixed(2)}`
            : "";
          const assignedToName = String(
            (approvedRoot as any)?.assigned_to_name ??
            effOrder?.assigned_to_name ??
            effOrder?.project?.assigned_to_name ??
            ""
          ).trim();
          const orderSummary = resolveApprovedProofVersionSummary(approvedRoot, version, first);
          return {
            version,
            files: compactProofFiles,
            approvedByName,
            approvedAtLabel,
            assignedToName,
            shippingName,
            shippingDesc,
            shippingAmountLabel,
            orderSummary,
          };
        });
    }, [approvedMessageData, effOrder]);

    const productSubtotalFromItems = useMemo(() => {
      if (!isInstall && !isRemoval) return 0;
      return computeJobProductSubtotalFromItems(items);
    }, [isInstall, isRemoval, items]);

    const productTaxFromItems = useMemo(() => {
      if (!isInstall && !isRemoval) return 0;
      return computeJobProductTaxFromItems(items);
    }, [isInstall, isRemoval, items]);

    const subTotal = useMemo(() => {
      if (isSales) return computeSalesSubtotalFromItems(items);
      return computeInvoiceJobSubtotal(isInstall, isRemoval, effOrder, productSubtotalFromItems);
    }, [isSales, isInstall, isRemoval, effOrder, items, productSubtotalFromItems]);

    // const shippingAmount = isSales
    //   ? Number(effOrder?.shipping_amount || effOrder?.shipping_method?.cost || 0)
    //   : 0;


    // Calculate special fees total and their tax
    const shippingAmount = useMemo(() => computeShippingAmountFromOrder(effOrder), [effOrder]);

    const specialFeesData = useMemo(() => computeSpecialFeesFromOrder(effOrder), [effOrder]);

    const invoiceShippingRowsForLabel = useMemo(() => {
      const fees = effOrder?.order_charges?.[0]?.shipping_option || effOrder?.shipping_option || [];
      return Array.isArray(fees) ? fees : [];
    }, [effOrder]);

    const invoiceIsUpsForLabel = useMemo(() => {
      const so = invoiceShippingRowsForLabel[0] as any;
      if (!so) return false;
      const rawId = String(so?.shipping_id ?? so?.shipping__id ?? "");
      if (rawId && /^[0-9A-Z]{2,3}$/.test(rawId) && rawId.length <= 3 && rawId !== "custom-manual") return true;
      return /^ups$/i.test(String(so?.shipping_type ?? "").trim());
    }, [invoiceShippingRowsForLabel]);

    const invoiceSpecialFeeRowsForLabel = useMemo(() => {
      const fees = effOrder?.order_charges?.[0]?.specialfee || effOrder?.specialfee || [];
      return Array.isArray(fees) ? fees : [];
    }, [effOrder]);

    const salesShippingChargeLabel = useMemo(
      () => formatShippingChargeOrderTotalsLabel(invoiceShippingRowsForLabel, { isUps: invoiceIsUpsForLabel }),
      [invoiceShippingRowsForLabel, invoiceIsUpsForLabel]
    );
    const invoiceSpecialFeeLabel = useMemo(
      () => formatSpecialFeeOrderTotalsLabel(invoiceSpecialFeeRowsForLabel),
      [invoiceSpecialFeeRowsForLabel]
    );

    const taxAmount = useMemo(() => {
      if (isSales) return computeSalesTaxAmount(items, effOrder, specialFeesData.tax);
      return computeInstallationRemovalTaxAmount(
        isInstall,
        isRemoval,
        effOrder,
        specialFeesData.tax,
        productTaxFromItems
      );
    }, [isSales, isInstall, isRemoval, effOrder, items, specialFeesData.tax, productTaxFromItems]);

    const discountAmount = useMemo(() => {
      if (isSales) return Number(effOrder?.discount_amount || 0);
      return computeInstallationRemovalDiscountAmount(isInstall, isRemoval, effOrder, items);
    }, [isSales, isInstall, isRemoval, effOrder, items]);

    const jobDiscountCode = useMemo(() => {
      if (isSales) return "";
      return resolveInstallationRemovalDiscountCode(effOrder, items);
    }, [isSales, effOrder, items]);

    // Grand Total = Sub_total + Shipping + specialfee + Tax - discount_amount
    const grandTotal = Number(
     (subTotal + shippingAmount.total + specialFeesData.total + taxAmount - discountAmount)
    );

    // console.log("Computed totals:", {
    //   subTotal,
    //   shippingAmount: shippingAmount.total,
    //   specialFeesTotal: specialFeesData.total,
    //   taxAmount,
    //   discountAmount,
    //   grandTotal,
    // });

    const invoiceAlreadyPaid = useMemo(
      () => sumOrderPaymentInfoAmountPaid(effOrder),
      [effOrder?.payment_info]
    );

    const invoiceRemainingBalance = Math.max(
      0,
      Math.round((grandTotal - invoiceAlreadyPaid) * 100) / 100
    );
    const invoiceOrderKind = publicOrderNormalizeKindFromFlags(isRemoval, isInstall);
    const invoiceOrderLabel = String(
      effOrder?.order_number ?? effOrder?.job_number ?? effOrder?.invoice_number ?? ""
    ).trim();

    const p = pickLatestOrderPaymentInfo(effOrder);

    /* ---------------------- preview / print (browser window) ---------------------- */

    const openWindowWithHtml = (shouldPrint: boolean) => {
      if (!printRootRef.current) return;
      const html = printRootRef.current.outerHTML;

      const inlinePrintCSS = `
  <style>
    @page { size: A4 portrait; margin: 10mm 10mm; }
    @media print {
      body * { visibility: hidden !important; }
      #invoice-pdf, #invoice-pdf * { visibility: visible !important; }
      #invoice-pdf {
        position: absolute !important; left: 0 !important; top: 0 !important;
        width: 794px !important; max-width: 794px !important; margin: 0 auto !important;
      }
      .container, .invoice-root { max-width: 794px !important; padding-left: 0 !important; padding-right: 0 !important; margin: 0 !important; }
      .row { display:flex !important; flex-wrap:wrap !important; width:100% !important; }
      .col-12 { width:100% !important; }
      .col-md-6 { width:50% !important; flex: 0 0 50% !important; max-width: 50% !important; }
      .col-lg-6 { width:50% !important; }
      .col-lg-5 { width:41.6667% !important; }
      .col-lg-4 { width:33.3333% !important; }
      .col-lg-3 { width:25% !important; }
      .table { break-inside: auto; page-break-inside: auto; }
      .table thead { display: table-header-group !important; }
      .table tfoot { display: table-footer-group !important; }

      tr.invoice-item-row,
      tr.invoice-item-row > td,
      .invoice-kv-row,
      .status100-total-line,
      .status100-payment-line,
      .invoice-footer-line {
        break-inside: avoid !important;
        page-break-inside: avoid !important;
      }

      .invoice-notes-box-full,
      .status100-payment-card,
      .invoice-footer-terms-block,
      .invoice-footer-powered,
      .invoice-payment-totals-row,
      .invoice-footer-panel {
        break-inside: auto !important;
        page-break-inside: auto !important;
      }

      .d-print-none { display: none !important; }
      #print-page-footer {
        position: fixed !important; bottom: 8mm !important; left: 0; right: 0;
        text-align: center; font-size: 11px; color: #6b7280;
      }
      #print-page-footer .num::after { content: counter(page); }
      #print-page-footer .den::after { content: counter(pages); }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
`;

      const fullHtml =
        "<!doctype html><html><head><meta charset='utf-8'/>" +
        "<meta name='viewport' content='width=device-width, initial-scale=1'/>" +
        "<title>Invoice</title>" +
        "<link rel='stylesheet' href='https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css'/>" +
        inlinePrintCSS +
        "</head><body>" +
        html +
        "</body></html>";

      try {
        const blob = new Blob([fullHtml], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        const win = window.open(url, "_blank", "noopener,noreferrer,width=900,height=1200");
        if (!win) return;
        if (shouldPrint) setTimeout(() => { win.focus(); win.print(); }, 350);
      } catch {
        const win = window.open("", "_blank", "noopener,noreferrer,width=900,height=1200");
        if (!win) return;
        win.document.open();
        win.document.write(fullHtml);
        win.document.close();
        if (shouldPrint) setTimeout(() => { win.focus(); win.print(); }, 350);
      }
    };

    useImperativeHandle(ref, () => ({
      preview: () => openWindowWithHtml(false),
      print: () => openWindowWithHtml(true),
    }));

    // 1) Build/choose the map (props wins; else hook) — use same line list as rendered rows + synthetic image ids
    const mapFromHook = useVariantImageUrlMap(lineItemsForImageMap, invoiceAuthToken) ?? {};
    const mapToUse: Record<string, string> = { ...(mapFromHook || {}), ...(effImageUrlMap || {}) };

    // 2) Safe URL helper — getImageSrc expects presign map, not auth headers
    const toUrl = (idOrUrl?: string): string => {
      if (!idOrUrl) return "";
      const v = String(idOrUrl).trim();
      if (/^(https?:)?\/\//i.test(v) || /^data:/i.test(v)) return v;
      return (
        (getImageSrc(v, mapToUse || {}) as string | undefined) ||
        mapToUse[v] ||
        ""
      );
    };

    // 3) Final resolver for item image — design preview only when design_id exists; else product images (matches Sales/Order Summary)
    const resolveItemImage = (item: any): string => {
      const designId = String(item?.design_id || item?.studio_design_id || "").trim();
      const previewId = String(item?.preview_url || item?.studio_preview_id || "").trim();
      if (designId && previewId) {
        const designPreview =
          toUrl(mapToUse[previewId]) ||
          toUrl(getImageSrc(previewId, mapToUse || {}) as string | undefined) ||
          toUrl(previewId);
        if (designPreview) return designPreview;
      }

      const imgs = Array.isArray(item?.images) ? item.images : [];
      const preferred = imgs.find((im: any) => im?.default) ?? imgs[0];

      const byItem = toUrl(preferred?.url);
      if (byItem) return byItem;

      const byImageUrl = toUrl(item?.image_url);
      if (byImageUrl) return byImageUrl;

      if (preferred?.url) {
        const viaMapByImage = toUrl(mapToUse[String(preferred.url)]);
        if (viaMapByImage) return viaMapByImage;
      }

      const byVariant = toUrl(mapToUse[String(item?.variant_id ?? "")]);
      if (byVariant) return byVariant;

      const byProduct = toUrl(mapToUse[String(item?.product_id ?? "")]);
      if (byProduct) return byProduct;

      return "";
    };

    const resolveItemImageFileId = (item: any): string => {
      const designId = String(item?.design_id || item?.studio_design_id || "").trim();
      const previewId = String(item?.preview_url || item?.studio_preview_id || "").trim();
      if (designId && is24Hex(previewId)) {
        return previewId;
      }

      const imgs = Array.isArray(item?.images) ? item.images : [];
      const preferred = imgs.find((im: any) => im?.default) ?? imgs[0];
      const preferredUrl = String(preferred?.url || "").trim();
      if (is24Hex(preferredUrl)) {
        return preferredUrl;
      }

      const imageUrl = String(item?.image_url || "").trim();
      if (is24Hex(imageUrl)) {
        return imageUrl;
      }

      return "";
    };

    // Auto-print if navigated with state.autoPrint
    useEffect(() => {
      if ((state as any)?.autoPrint) {
        setTimeout(() => window.print(), 250);
      }
    }, [state]);

    // Raw logo value from template (prefer new schema, then legacy)

    // Resolve to a usable URL
    const effTplLogoUrl = React.useMemo(() => {
      const v = String(tplLogoRaw || "").trim();
      if (!v) return "";
      if (/^(https?:|data:|blob:)/i.test(v)) return v;         // absolute OK
      const viaMap = (mapFromHook1 && mapFromHook1[v]) || "";  // presigned from hook
      return viaMap || (getImageSrc(v, mapFromHook1 || {}) as string) || "";
    }, [tplLogoRaw, mapFromHook1]);

    const billingLines = useMemo(() => formatInvoiceAddressLines(billing), [billing]);

    const shippingLines = useMemo(
      () =>
        formatInvoiceAddressLines(
          isSales ? shipping : isInstall ? installAddress : removalAddress
        ),
      [isSales, isInstall, shipping, installAddress, removalAddress]
    );

    /** Order No: `order_number` / `orderNumber`; if empty on install/removal, `job_number` / `jobNumber`. */
    const displayOrderNo = useMemo(() => {
      let raw = effOrder?.order_number ?? effOrder?.orderNumber;
      if ((raw == null || String(raw).trim() === "") && !isSales) {
        raw = effOrder?.job_number ?? effOrder?.jobNumber;
      }
      if (raw == null || String(raw).trim() === "") return "";
      return String(raw).trim();
    }, [effOrder, isSales]);

    /** Order Date: `order_date` / `orderDate`, else `created_at`; missing → "—". */
    const displayOrderDateMdY = useMemo(() => {
      let d = parseOrderDateValue(effOrder?.order_date ?? effOrder?.orderDate);
      if (!d) d = parseOrderDateValue(effOrder?.created_at);
      return formatMdY(d);
    }, [effOrder]);

    /** PO No: `po_number` / `poNumber`; missing → "—". */
    const displayPONo = useMemo(() => {
      const raw = effOrder?.po_number ?? effOrder?.poNumber;
      if (raw == null || String(raw).trim() === "") return "—";
      return String(raw).trim();
    }, [effOrder]);

    const displayProjectNo = useMemo(() => {
      if (!isSales && !isInstall) return "";
      const raw =
        effOrder?.project_number ??
        effOrder?.projectNumber ??
        effOrder?.project?.project_number ??
        effOrder?.project?.projectNumber;
      if (raw == null || String(raw).trim() === "") return "";
      return String(raw).trim();
    }, [effOrder, isSales, isInstall]);

    /**
     * Invoice No (display): `invoice_prefix` + `order_number` (e.g. SRI00267).
     */
    const displayInvoiceNo = useMemo(
      () => formatInvoiceDisplayNumber(effOrder),
      [effOrder]
    );

    /** Invoice Date: only `invoice_date` / `invoiceDate`; missing → "—". */
    const displayInvoiceDateMdY = useMemo(() => {
      const d = parseOrderDateValue(effOrder?.invoice_date ?? effOrder?.invoiceDate);
      return formatMdY(d);
    }, [effOrder]);

    const displayPaymentStatus = String(effOrder?.payment_status || "Awaiting Payment")
      .replace(/_/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    const printTimeMdY = useMemo(() => {
      const d = printedAt;
      if (!d || Number.isNaN(d.getTime())) return "—";
      const datePart = `${d.getMonth() + 1}-${d.getDate()}-${d.getFullYear()}`;
      const timePart = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
      return `${datePart} ${timePart}`;
    }, [printedAt]);

    const notesDisplay = useMemo(
      () => computeInvoiceNotesDisplay(isSales, isInstall, isRemoval, effOrder),
      [isSales, isInstall, isRemoval, effOrder]
    );

    const notesCardText =
      notesDisplay != null && String(notesDisplay).trim() !== ""
        ? String(notesDisplay)
        : isSales
          ? "No notes on this order."
          : "No job instructions or notes.";

    const termsCombinedText = [effServiceInfo?.termsCancellation, effServiceInfo?.termsService]
      .filter((t) => t != null && String(t).trim() !== "")
      .join("\n\n");

    const downloadInvoicePdf = useCallback(async () => {
      if (downloadingPdf) return;
      const tokenForPdf = String(
        query.get("access_token") ||
        query.get("token") ||
        getAuthCookie("sessionToken") ||
        ""
      ).trim();
      const resolvedOrderId = String(
        query.get("order_id") ||
        effOrder?._id ||
        effOrder?.id ||
        effOrder?.order_id ||
        ""
      ).trim();
      if (!resolvedOrderId || !tokenForPdf) return;

      setDownloadingPdf(true);
      try {
        const fileLabel = String(
          formatInvoiceDisplayNumber(effOrder) ||
          resolvedOrderId
        ).trim();
        const attachment = isRemoval
          ? await buildRemovalOrderInvoicePdfAttachment({
            removalOrderId: resolvedOrderId,
            token: tokenForPdf,
            fileLabel,
          })
          : isInstall
            ? await buildInstallationOrderInvoicePdfAttachment({
              installationOrderId: resolvedOrderId,
              token: tokenForPdf,
              fileLabel,
            })
            : await buildSalesOrderInvoicePdfAttachment({
              salesOrderId: resolvedOrderId,
              token: tokenForPdf,
              fileLabel,
            });
        const out = attachmentMapToBlob(attachment);
        if (!out) return;
        const url = URL.createObjectURL(out.blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = out.name || "Invoice.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } finally {
        setDownloadingPdf(false);
      }
    }, [downloadingPdf, query, effOrder, isRemoval, isInstall]);

    return (
      <>
        <div className="invoice-preview-page w-100">
          <div
            className="content invoice-print-root"
            style={{ padding: 16, fontFamily: "Arial, Helvetica, sans-serif" }}
          >
          <div id="invoice-pdf" className="invoice-pdf-root pdf-export">
            {showInvoiceActionBar ? (
              <div
                className="d-print-none no-print d-flex justify-content-between align-items-center"
                style={{ maxWidth: 940, margin: "0 auto 12px" }}
              >
                <button className="vd-back no-print" type="button" onClick={() => window.history.back()}>
                  <i className="bi bi-arrow-left me-1" /> Back
                </button>
                <button
                  className="btn btn-primary table-addbutton no-print"
                  type="button"
                  onClick={downloadInvoicePdf}
                  disabled={downloadingPdf}
                  style={
                    downloadingPdf
                      ? {
                          cursor: "not-allowed",
                          backgroundColor: "#155dfc",
                          borderColor: "#155dfc",
                          color: "#fff",
                          opacity: 1,
                        }
                      : undefined
                  }
                >
                  <i className="bi bi-download me-2" /> {downloadingPdf ? "Downloading..." : "Download"}
                </button>
              </div>
            ) : null}
            <div className="invoice-root invoice-print-inner" ref={printRootRef}>
              <style>{INVOICE_STATUS100_SHELL_STYLES}</style>
              <style>{SCREEN_STYLES}</style>

              <div className="status100-shell">
                {isPublicCustomerInvoiceOpen && publicInvoiceOrderLoading ? (
                  <div
                    className="status100-pad text-center"
                    style={{ padding: "32px 16px", color: "#6b7280", fontSize: 14 }}
                  >
                    Loading invoice…
                  </div>
                ) : null}
                <div className="status100-hero status100-pad mt-3">
                  <div className="status100-hero-title-wrap invoice-title-row">
                    <div className="status100-title d-flex justify-content-center">
                      {titleWords.map((word, idx) => (
                        <span
                          key={`${word}-${idx}`}
                          className={`invoice-title-word${idx === titleWords.length - 1 ? " is-last" : ""}`}
                        >
                          {word}
                          {idx < titleWords.length - 1 ? "\u00A0" : ""}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="status100-brand">
                    {effTplLogoUrl2 ? (
                      <img
                        src={effTplLogoUrl2}
                        data-pdf-file-id={logoIds[0] || undefined}
                        // alt={effServiceInfo?.companyName || ""}
                      />
                    ) : (
                      <div className="status100-brand-name">{osShow(effServiceInfo?.companyName)}</div>
                    )}
                  </div>
                  <div className="status100-header-right">
                    <div className="status100-meta-grid">
                      <span className="status100-meta-label invoice-pdf-meta-label">
                        <span className="invoice-pdf-meta-label-word">Invoice</span>
                        <span className="invoice-pdf-meta-label-word invoice-pdf-meta-label-word-second">No</span>
                      </span>
                      <span className="status100-meta-value">#{osShow(displayInvoiceNo)}</span>
                      <span className="status100-meta-label invoice-pdf-meta-label">
                        <span className="invoice-pdf-meta-label-word">Invoice</span>
                        <span className="invoice-pdf-meta-label-word invoice-pdf-meta-label-word-second">Date</span>
                      </span>
                      <span className="status100-meta-value"># {displayInvoiceDateMdY}</span>
                      <span className="status100-meta-label invoice-pdf-meta-label">
                        <span className="invoice-pdf-meta-label-word">Order</span>
                        <span className="invoice-pdf-meta-label-word invoice-pdf-meta-label-word-second">No</span>
                      </span>
                      <span className="status100-meta-value">#{osShow(displayOrderNo)}</span>
                      <span className="status100-meta-label invoice-pdf-meta-label">
                        <span className="invoice-pdf-meta-label-word">Order</span>
                        <span className="invoice-pdf-meta-label-word invoice-pdf-meta-label-word-second">Date</span>
                      </span>
                      <span className="status100-meta-value"># {displayOrderDateMdY}</span>
                      <span className="status100-meta-label invoice-pdf-meta-label">
                        <span className="invoice-pdf-meta-label-word">PO</span>
                        <span className="invoice-pdf-meta-label-word invoice-pdf-meta-label-word-second">No</span>
                      </span>
                      <span className="status100-meta-value">{osShow(displayPONo)}</span>
                      {displayProjectNo ? (
                        <>
                          <span className="status100-meta-label invoice-pdf-meta-label">
                            <span className="invoice-pdf-meta-label-word">Project</span>
                            <span className="invoice-pdf-meta-label-word invoice-pdf-meta-label-word-second">No</span>
                          </span>
                          <span className="status100-meta-value">#{displayProjectNo}</span>
                        </>
                      ) : null}
                      {/* <span className="status100-meta-label">Print time</span>
                      <span className="status100-meta-value"># {printTimeMdY}</span> */}
                    </div>
                    <div className="status100-badge">{displayPaymentStatus}</div>
                  </div>
                </div>

                <div className="status100-padcustom">
                  <div className="status100-section status100-section--customer">
                    <div className="status100-section-head">
                      <div style={sectionIconBox}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </div>
                      <div style={sectionTitle}>Customer Information</div>
                    </div>
                    <div className="status100-card status100-customer-box">
                      <div className="status100-customer-grid">
                        <div>
                          <div style={fieldLabel}>Brokerage</div>
                          <div style={fieldValue}>{osShow(brokerage)}</div>
                        </div>
                        <div>
                          <div style={fieldLabel}>Office</div>
                          <div style={fieldValue}>{osShow(office)}</div>
                        </div>
                        <div>
                          <div style={fieldLabel}>{salesAgentOrTeamMemberLabel}</div>
                          <div style={fieldValue}>{osShow(agent)}</div>
                        </div>
                        <div className="status100-email-cell">
                          <div style={fieldLabel}>Email</div>
                          <div style={fieldValue}>
                            {email ? (
                              <a className="status100-email-link" href={`mailto:${email}`}>
                                {email}
                              </a>
                            ) : (
                              "—"
                            )}
                          </div>
                        </div>
                        <div>
                          <div style={fieldLabel}>Phone</div>
                          <div style={fieldValue}>{osShow(phone)}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="status100-section status100-section--delivery">
                    <div className="status100-section-head">
                      <div style={sectionIconBox}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 1 1 18 0Z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                      </div>
                      <div style={sectionTitle}>Delivery Details</div>
                    </div>
                    <div className="status100-address-grid">
                      <div className="status100-card status100-address-card">
                        <div className="status100-address-label">Billing Address</div>
                        {billingLines.map((line, index) => (
                          <div
                            key={`billing-${index}`}
                            className={`status100-address-line ${index === 0 ? "is-name" : ""}`}
                          >
                            {line}
                          </div>
                        ))}
                      </div>
                      <div className="status100-card status100-address-card">
                        <div className="status100-address-label">
                          {deliveryAddressLabelForOrder(effType, effOrder)}
                        </div>
                        {shippingLines.map((line, index) => (
                          <div
                            key={`ship-${index}`}
                            className={`status100-address-line ${index === 0 ? "is-name" : ""}`}
                          >
                            {line}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {!isSales ? (
                    <div className="status100-section">
                      <div className="status100-section-head">
                        <div style={sectionIconBox}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="2">
                            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                          </svg>
                        </div>
                        <div style={sectionTitle}>{jobDetailsSectionTitle(isInstall)}</div>
                      </div>
                      <div className="status100-card status100-customer-box">
                        <div className="status100-customer-grid" style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
                          <div>
                            <div style={fieldLabel}>Scheduled Date</div>
                            <div style={fieldValue}>{dateOnly(scheduled)}</div>
                          </div>
                          <div>
                            <div style={fieldLabel}>Assigned To</div>
                            <div style={fieldValue}>
                              {show(getInstallerNameForJobInvoice(isInstall, effOrder))}
                            </div>
                          </div>
                          <div>
                            <div style={fieldLabel}>Priority</div>
                            <div style={fieldValue}>
                              {(() => {
                                const label = priorityText(effOrder?.priority);
                                return (
                                  <span className={`priority-pill ${String(label).toLowerCase()}`}>
                                    {show(label)}
                                  </span>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                        {show(getGateAccessCodeForJobInvoice(isInstall, effOrder)) !== "---" ? (
                          <div style={{ marginTop: 16 }}>
                            <div style={fieldLabel}>Gate Code</div>
                            <div style={fieldValue}>
                              {show(getGateAccessCodeForJobInvoice(isInstall, effOrder))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                  <div className="status100-section">
                    <div className="status100-section-head">
                      <div style={sectionIconBox}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="16" rx="2" />
                          <path d="M3 10h18" />
                        </svg>
                      </div>
                      <div style={sectionTitle}>{isSales ? "Order Items" : "Line Items"}</div>
                    </div>

                    {isSales ? (
                      <>
                        <div className="rdt-desktop">
                          <div className="status100-table-wrap">
                            <table className="status100-table invoice-pdf-items-table">
                              <thead>
                                <tr>
                                  <th className="invoice-pdf-col-image">Image</th>
                                  <th className="invoice-pdf-col-product">Product</th>
                                  <th className="invoice-pdf-col-details">Details</th>
                                  <th className="status100-qty invoice-pdf-col-qty">Qty</th>
                                  <th className="status100-right invoice-pdf-col-changed" title="Unit Price">
                                    Unit<br />Price
                                  </th>
                                  <th className="status100-center invoice-pdf-col-discount">Discount</th>
                                  {/* <th className="status100-right invoice-pdf-col-tax">Tax</th> */}
                                  <th className="status100-right invoice-pdf-col-total">Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {items.map((it, idx) => {
                                  const qty = Number(it?.quantity || 0);
                                  const unitPriceDisplay = getSalesOrderLineUnitRetailPrice(it);
                                  const itemTax = Number(it?.item_tax_info?.total_product_tax_amount || 0);
                                  const lineDiscount = getSalesOrderLineDiscount(it);
                                  const total = getSalesOrderLineTotalAmount(it);
                                  const src = resolveItemImage(it);
                                  const pdfFileId = resolveItemImageFileId(it);
                                  const skuDisp = it?.sku?.includes("_")
                                    ? it?.sku.split("_").slice(0, -1).join("_")
                                    : it?.sku;
                                  return (
                                    <tr className="invoice-item-row" key={it?.variant_id || it?.product_id || idx}>
                                      <td className="invoice-pdf-col-image">
                                        <div className="invoice-pdf-image-cell-wrap">
                                          <InvoiceStatus100Thumb
                                            imageUrl={src}
                                            pdfFileId={pdfFileId}
                                            imageAlt={String(it?.product_name || it?.name || "Product")}
                                            productName={String(it?.product_name || it?.name || "")}
                                          />
                                        </div>
                                      </td>
                                      <td className="invoice-pdf-col-product">
                                        <div className="status100-product-name">{osShow(it?.product_name || it?.name)}</div>
                                        <div className="status100-product-sku">SKU: {osShow(skuDisp)}</div>
                                        {it?.custom_attributes && (
                                          <div className="status100-product-detail">
                                            {Object.entries(it.custom_attributes as Record<string, any>).map(([key, value]) => (
                                              <div key={key}>
                                                <strong>Description:</strong>{" "}
                                                {typeof value === "object" ? JSON.stringify(value) : String(value)}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        <LineItemCustomizations item={it} imageUrlMap={mapFromHook1} />
                                        {(isSales || isInstall) && <LineItemCustomInformation item={it} />}
                                      </td>
                                      <td className="invoice-pdf-col-details">
                                        {it?.inventory_source !== "custom" && <LineItemAttributes item={it} />}
                                      </td>
                                      <td className="status100-qty invoice-pdf-col-qty">{osShow(qty)}</td>
                                      <td className="status100-right invoice-pdf-col-changed">{money(unitPriceDisplay)}</td>
                                      <td className="status100-center invoice-pdf-col-discount">
                                        <DiscountAmountDisplay amount={lineDiscount} />
                                      </td>
                                      {/* <td className="status100-right invoice-pdf-col-tax">{money(itemTax)}</td> */}
                                      <td className="status100-right status100-total-bold invoice-pdf-col-total">{money(total)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <div className="rdt-mobile">
                          <div className="status100-mobile-items">
                            {items.map((it, idx) => {
                              const qty = Number(it?.quantity || 0);
                              const unitPriceDisplay = getSalesOrderLineUnitRetailPrice(it);
                              const itemTax = Number(it?.item_tax_info?.total_product_tax_amount || 0);
                              const lineDiscount = getSalesOrderLineDiscount(it);
                              const total = getSalesOrderLineTotalAmount(it);
                              const src = resolveItemImage(it);
                              const pdfFileId = resolveItemImageFileId(it);
                              const skuDisp = it?.sku?.includes("_")
                                ? it?.sku.split("_").slice(0, -1).join("_")
                                : it?.sku;
                              return (
                                <div key={it?.variant_id || it?.product_id || idx} className="po-card">
                                  <div className="status100-mobile-item-head">
                                    <InvoiceStatus100Thumb
                                      imageUrl={src}
                                      pdfFileId={pdfFileId}
                                      imageAlt={String(it?.product_name || it?.name || "Product")}
                                      productName={String(it?.product_name || it?.name || "")}
                                    />
                                    <div className="status100-mobile-item-head-main">
                                      <div className="status100-product-name">{osShow(it?.product_name || it?.name)}</div>
                                      <div className="status100-product-sku">SKU: {osShow(skuDisp)}</div>
                                      {it?.custom_attributes && (
                                        <div className="status100-product-detail">
                                          {Object.entries(it.custom_attributes as Record<string, any>).map(([key, value]) => (
                                            <div key={key}>
                                              <strong>Description:</strong>{" "}
                                              {typeof value === "object" ? JSON.stringify(value) : String(value)}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      <LineItemCustomizations item={it} imageUrlMap={mapFromHook1} />
                                      <LineItemCustomInformation item={it} />
                                    </div>
                                  </div>
                                  <div className="status100-mobile-item-fields">
                                    <div className="po-field">
                                      <div className="po-field-label">Details</div>
                                      <div className="po-field-value">
                                        {it?.inventory_source !== "custom" && <LineItemAttributes item={it} />}
                                      </div>
                                    </div>
                                    <div className="po-field">
                                      <div className="po-field-label">Qty</div>
                                      <div className="po-field-value">{osShow(qty)}</div>
                                    </div>
                                    <div className="po-field">
                                      <div className="po-field-label">Unit Price</div>
                                      <div className="po-field-value">{money(unitPriceDisplay)}</div>
                                    </div>
                                    <div className="po-field">
                                      <div className="po-field-label">Discount</div>
                                      <div className="po-field-value">
                                        <DiscountAmountDisplay amount={lineDiscount} />
                                      </div>
                                    </div>
                                    <div className="po-field">
                                      <div className="po-field-label">Tax</div>
                                      <div className="po-field-value">{money(itemTax)}</div>
                                    </div>
                                    <div className="po-field">
                                      <div className="po-field-label">Total</div>
                                      <div className="po-field-value status100-total-bold">{money(total)}</div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="rdt-desktop">
                          <div className="status100-table-wrap">
                            <table className="table invoice-pdf-items-table invoice-pdf-3col-table status100-table mb-0">
                              <colgroup>
                                <col style={{ width: "18%" }} />
                                <col style={{ width: "44%" }} />
                                <col style={{ width: "22%" }} />
                                <col style={{ width: "16%" }} />
                              </colgroup>

                              <thead>
                                <tr>
                                  <th className="invoice-pdf-col-image">Image</th>
                                  <th className="invoice-pdf-col-product">Item Details</th>
                                  <th className="invoice-pdf-col-details">SKU</th>
                                  <th className="invoice-pdf-col-qty">Qty</th>
                                </tr>
                              </thead>

                              <tbody>
                                {items.map((it: any, idx: number) => {
                                  const jobLineSrc = resolveItemImage(it);
                                  const jobLinePdfId = resolveItemImageFileId(it);
                                  return (
                                  <tr key={it?.variant_id || it?.product_id || idx} className="invoice-item-row">
                                    <td className="d-flex justify-content-center">
                                      <div className="">
                                        <InvoiceStatus100Thumb
                                          imageUrl={jobLineSrc}
                                          pdfFileId={jobLinePdfId}
                                          imageAlt={String(it?.product_name || it?.name || "Product")}
                                          productName={String(it?.product_name || it?.name || "")}
                                        />
                                      </div>
                                    </td>
                                    <td className="invoice-pdf-col-product">
                                      <div className="invoice-cell-center">
                                        <div className="status100-product-name">
                                          {show(it?.product_name || it?.name || "---")}
                                        </div>
                                        <LineItemCustomizations item={it} imageUrlMap={mapFromHook1} />
                                      <LineItemCustomInformation item={it} />
                                      </div>
                                    </td>

                                    <td className="invoice-pdf-col-details">
                                      <div className="invoice-cell-center">
                                        <div className="meta-value">
                                          {show(it?.product_type || it?.sku || it?.product_sku || "---")}
                                        </div>
                                      </div>
                                    </td>

                                    <td className="invoice-pdf-col-qty">
                                      <div className="invoice-cell-center">
                                        <div className="meta-value">{itemQty(it)}</div>
                                      </div>
                                    </td>
                                  </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div className="rdt-mobile">
                          <div className="status100-mobile-items">
                            {items.map((it, idx) => {
                              const qty = itemQty(it);
                              const isRotational = it?.is_rotational || it?.inventory_source === "rotational";
                              const serialCodes =
                                isRotational && Array.isArray(it?.serial_allocations)
                                  ? it.serial_allocations.map((s: any) => s?.serial_code).filter(Boolean).join(", ")
                                  : null;
                              const skuDisp = it?.sku?.includes("_")
                                ? it?.sku.split("_").slice(0, -1).join("_")
                                : it?.sku;
                              const jobMobSrc = resolveItemImage(it);
                              const jobMobPdfId = resolveItemImageFileId(it);
                              return (
                                <div key={it?.variant_id || it?.product_id || idx} className="po-card">
                                  <div className="status100-mobile-item-head">
                                    <InvoiceStatus100Thumb
                                      imageUrl={jobMobSrc}
                                      pdfFileId={jobMobPdfId}
                                      imageAlt={String(it?.product_name || it?.name || "Product")}
                                      productName={String(it?.product_name || it?.name || "")}
                                    />
                                    <div className="status100-mobile-item-head-main">
                                      <div className="status100-product-name">{show(it?.name || it?.product_name)}</div>
                                      {serialCodes ? (
                                        <div className="status100-product-detail">Serial #: {serialCodes}</div>
                                      ) : null}
                                      <LineItemCustomizations item={it} imageUrlMap={mapFromHook1} />
                                      <LineItemCustomInformation item={it} />
                                    </div>
                                  </div>
                                  <div className="po-field">
                                    <div className="po-field-label">SKU</div>
                                    <div className="po-field-value">{osShow(skuDisp)}</div>
                                  </div>
                                  <div className="po-field">
                                    <div className="po-field-label">Qty</div>
                                    <div className="po-field-value">{osShow(qty)}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <div style={{marginBottom: "30px",marginTop: "25px"}}>
                  {(isSales || isInstall) && linkedProjectId && hasApprovedMessage ? (
                    <div>
                      {approvedProofCards.map((card) => (
                        <CompactApprovedProofCard
                          key={`approved-proof-v-${card.version}`}
                          version={card.version}
                          files={card.files}
                          approvedBy={card.approvedByName}
                          approvedAt={card.approvedAtLabel !== "—" ? card.approvedAtLabel : ""}
                          footerBy={card.assignedToName}
                          shippingName={card.shippingName}
                          shippingDesc={card.shippingDesc}
                          shippingAmountLabel={card.shippingAmountLabel}
                          showOrderSummary={isSignagePortalRoute(String(loc?.pathname ?? ""))}
                          orderSummary={card.orderSummary}
                          onDownloadByFileId={(fileId, fileName) => {
                            void downloadWorkDriveFile(fileId, fileName);
                          }}
                        />
                      ))}
                    </div>
                  ) : null}
                  </div>

                  <div className="invoice-bottom-stack">
                    <div className="invoice-notes-full">
                      <div className="status100-section-head">
                        <div style={sectionIconBox}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16l4-2 4 2 4-2 4 2V8z" />
                            <path d="M14 2v6h6" />
                          </svg>
                        </div>
                        <div style={sectionTitle}>
                          {isSales ? "Order Notes" : "Job Instructions & Notes"}
                        </div>
                      </div>
                      <div className="status100-card status100-notes-box invoice-notes-box-full">{notesCardText}</div>
                    </div>

                    <div className="invoice-payment-totals-row">
                      <div className="status100-payment-card invoice-payment-half">
                        <div className="status100-total-heading">Payment Details</div>
                        <div className="status100-total-line status100-payment-line invoice-kv-row">
                          <span>Transaction ID</span>
                          <strong className="invoice-tx-wrap">{osShow(p?.payment_id)}</strong>
                        </div>
                        <div className="status100-total-line status100-payment-line invoice-kv-row">
                          <span>Payment Method</span>
                          <strong>{osShow(p?.method)}</strong>
                        </div>
                        {/* <div className="status100-total-line">
                          <span>Payment Date</span>
                          <strong>{dateOnly(p?.payment_date)}</strong>
                        </div> */}
                        <div className="status100-total-line status100-payment-line invoice-kv-row">
                          <span>Paid By</span>
                          <strong>{displayPaymentPaidByName(p)}</strong>
                        </div>
                        <div className="status100-grand invoice-amount-paid-box">
                          <div className="status100-grand-label">Amount Paid</div>
                          <div className="status100-grand-value">{showMoney(p?.amount_paid ?? p?.total_amount)}</div>
                        </div>
                      </div>

                      <div className="status100-payment-card invoice-order-half d-flex flex-column gap-3">
                        <div className="invoice-order-total-block">
                        <div className="status100-total-heading">{isSales ? "Order Total" : "Cost Breakdown"}</div>
                        {isSales ? (
                          <>
                            <div className="status100-total-line invoice-kv-row">
                              <span>Sub Total:</span>
                              <strong>{money(subTotal)}</strong>
                            </div>
                            {discountAmount > 0 ? (
                              <div className="status100-total-line invoice-kv-row">
                                <span >
                                  Discount
                                  <span style={{ color: "#00a63e" }}>
                                  {String(effOrder?.discount_code || "").trim()
                                    ? `:(${String(effOrder?.discount_code).trim()})`
                                    : ""}
                                    </span>
                                </span>
                                <strong style={{ color: "#00a63e" }}>-{money(discountAmount)}</strong>
                              </div>
                            ) : null}
                            <div className="status100-total-line invoice-kv-row">
                              <span className="text-break" style={{ maxWidth: "72%" }}>
                                {salesShippingChargeLabel}
                              </span>
                              <strong>{money(shippingAmount.total)}</strong>
                            </div>
                            {specialFeesData.total > 0 ? (
                              <div className="status100-total-line invoice-kv-row">
                                <span className="text-break" style={{ maxWidth: "72%" }}>
                                  {invoiceSpecialFeeLabel}
                                </span>
                                <strong>{money(specialFeesData.total)}</strong>
                              </div>
                            ) : null}
                            <div className="status100-total-line invoice-kv-row">
                              <span>Tax</span>
                              <strong>{money(taxAmount)}</strong>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="status100-total-line invoice-kv-row">
                              <span>{serviceCostLineLabel(isInstall)}</span>
                              <strong>{money(effOrder?.installation_cost ?? effOrder?.removal_cost ?? 0)}</strong>
                            </div>
                            {isInstall ? (
                              <div className="status100-total-line invoice-kv-row">
                                <span>Unit Cost</span>
                                <strong>{money(productSubtotalFromItems)}</strong>
                              </div>
                            ) : null}
                            <div className="status100-total-line invoice-kv-row">
                              <span>Sub-total</span>
                              <strong>{money(subTotal)}</strong>
                            </div>
                            {specialFeesData.total > 0 ? (
                              <div className="status100-total-line invoice-kv-row">
                                <span className="text-break" style={{ maxWidth: "72%" }}>
                                  {invoiceSpecialFeeLabel}
                                </span>
                                <strong>{money(specialFeesData.total)}</strong>
                              </div>
                            ) : null}
                            <div className="status100-total-line">
                              <span>Tax</span>
                              <strong>{money(taxAmount)}</strong>
                            </div>
                            {discountAmount > 0 ? (
                              <div className="status100-total-line">
                                <span style={{ color: "#dc2626" }}>
                                  Discount
                                  {jobDiscountCode ? `:(${jobDiscountCode})` : ""}
                                </span>
                                <strong style={{ color: "#dc2626" }}>-{money(discountAmount)}</strong>
                              </div>
                            ) : null}
                          </>
                        )}
                        <div className="status100-grand invoice-grand-total-box">
                          <div className="status100-grand-label">Grand Total</div>
                          <div className="status100-grand-value">{`${money(grandTotal)}`}</div>
                        </div>
                        <div
                          className="invoice-payment-balance-lines"
                          style={{ borderTop: "1px solid #e5e7eb", marginTop: 12, paddingTop: 12 }}
                        >
                          <div className="status100-total-line invoice-kv-row">
                            <span>Already Paid</span>
                            <strong style={{ color: "#00a63e " }}>{`${money(invoiceAlreadyPaid)}`}</strong>
                          </div>
                          <div className="status100-total-line invoice-kv-row">
                            <span>Remaining Balance</span>
                            <strong style={{ color: "#f06548" }}>
                              {String(effOrder?.payment_status || "") === "Overpaid"
                                ? `+${money(Math.abs(grandTotal - invoiceAlreadyPaid))}`
                                : `${money(invoiceRemainingBalance)}`}
                            </strong>
                          </div>
                        </div>
                        </div>
                        {!isPrivateInvoicePreview && queryOrderId && (isSales || isInstall || isRemoval) ? (
                          <PublicOrderPayUi
                            orderKind={invoiceOrderKind}
                            isPrivatePreview={isPrivateInvoicePreview}
                            companyName={effServiceInfo?.companyName}
                            orderLabel={invoiceOrderLabel}
                            onPublicOrderLoaded={setInvoicePublicOrder}
                            parentFetchesOrder={Boolean(queryOrderId && !isPrivateInvoicePreview)}
                            syncedPublicOrder={effOrder}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="invoice-footer-panel">
                  <div className="invoice-footer-grid">
                    <div className="invoice-footer-field">
                      <span className="invoice-footer-k">Company/Vendor Name</span>
                      <span className="invoice-footer-v">{osShow(effServiceInfo?.companyName)}</span>
                    </div>
                    <div className="invoice-footer-field">
                      <span className="invoice-footer-k">Contact</span>
                      <span className="invoice-footer-v">{osShow(effServiceInfo?.contactPhone)}</span>
                    </div>
                    <div className="invoice-footer-field">
                      <span className="invoice-footer-k">Website</span>
                      <span className="invoice-footer-v invoice-footer-website">
                        {effServiceInfo?.website ? (
                          <a
                            className="status100-footer-link"
                            href={
                              /^https?:\/\//i.test(String(effServiceInfo.website))
                                ? effServiceInfo.website
                                : `https://${effServiceInfo.website}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {effServiceInfo.website}
                          </a>
                        ) : (
                          "—"
                        )}
                      </span>
                    </div>
                    <div className="invoice-footer-field">
                      <span className="invoice-footer-k">Email</span>
                      <span className="invoice-footer-v">
                        {effServiceInfo?.email ? (
                          <a className="status100-footer-link" href={`mailto:${effServiceInfo.email}`}>
                            {effServiceInfo.email}
                          </a>
                        ) : (
                          "—"
                        )}
                      </span>
                    </div>
                    <div className="invoice-footer-field invoice-footer-field-address">
                      <span className="invoice-footer-k">Address</span>
                      <span className="invoice-footer-v">{osShow(effServiceInfo?.addressLine)}</span>
                    </div>
                  </div>

                  <div className="invoice-footer-terms-block">
                    <div className="invoice-footer-terms-title">Terms & Conditions</div>
                    <div className="invoice-footer-terms-body">
                      {termsCombinedText ? termsCombinedText : ""}
                    </div>
                  </div>

                  <div className="invoice-footer-powered">
                    <span className="status100-powered-lead">Powered by </span>
                    <a className="status100-powered-brand" href="https://realesy.com/" target="_blank" rel="noopener noreferrer">
                      RealEsy
                    </a>
                  </div>
                </div>
              </div>
              <div className="print-page-footer" aria-hidden="true" />
            </div>
          </div>
          </div>
        </div>
      </>
    );
  }
);

/* ------------------------------- styles ------------------------------- */
const SCREEN_STYLES = `
/* ==== Screen defaults ==== */
/* Fill signage/realtor main column; center preview in .main-content, not the full window */
.invoice-preview-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.invoice-print-root {
  width: fit-content;
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;
}
.invoice-pdf-root {
  width: 100%;
  max-width: 940px;
  margin-left: auto;
  margin-right: auto;
  // background: #fff;
  color: #212529;
}
.invoice-root { max-width: 980px; }
.invoice-title-row {
  white-space: nowrap;
  letter-spacing: normal;
  word-spacing: normal;
}
.invoice-title-word {
  display: inline-block;
}
.invoice-title-word:not(.is-last) {
  margin-right: 0.34em;
}
#invoice-pdf .status100-shell {
  width: 100% !important;
  max-width: 100% !important;
  overflow: visible !important;
}
#invoice-pdf .status100-hero {
  align-items: flex-start !important;
}
#invoice-pdf .status100-header-right {
  min-width: 320px !important;
  max-width: 52% !important;
}
#invoice-pdf .status100-meta-grid {
  grid-template-columns: 98px minmax(110px, 1fr) !important;
  column-gap: 6px !important;
  row-gap: 8px !important;
  align-items: center !important;
}
#invoice-pdf .status100-meta-grid .status100-meta-label,
#invoice-pdf .status100-meta-grid .status100-meta-value {
  white-space: nowrap !important;
  display: block !important;
  line-height: 1.35 !important;
}
#invoice-pdf .status100-meta-grid .status100-meta-label {
  text-align: left !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
}
#invoice-pdf .invoice-pdf-meta-label-word-second { padding-left: 4px !important; }
#invoice-pdf .status100-meta-grid .status100-meta-value {
  text-align: right !important;
  font-weight: 500 !important;
}

/*
 * Line items: same breakpoint as Order_summary / custom.css (1022px).
 * Screen ≤1021px: card layout (.rdt-mobile). Wider: desktop table (.rdt-desktop).
 * Print still hides .rdt-mobile via invoice shell @media print.
 */
@media screen and (max-width: 1021px) {
  #invoice-pdf .rdt-desktop {
    display: none !important;
  }
  #invoice-pdf .rdt-mobile {
    display: block !important;
  }
  #invoice-pdf .invoice-bottom-stack {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  #invoice-pdf .invoice-payment-totals-row {
    flex-direction: column !important;
    flex-wrap: wrap !important;
    align-items: stretch !important;
    gap: 16px !important;
  }
  #invoice-pdf .invoice-payment-totals-row .status100-payment-card {
    flex: 1 1 auto !important;
    width: 100% !important;
    max-width: 100% !important;
  }
  #invoice-pdf .status100-notes-box.invoice-notes-box-full {
    padding: 18px 18px 18px 20px;
    min-height: 0;
    box-shadow: none;
  }
  /* Tight label / value columns — avoid 1fr + min-width stretching on narrow screens */
  #invoice-pdf .status100-header-right {
    min-width: 0 !important;
    max-width: 100% !important;
  }
  #invoice-pdf .status100-meta-grid {
    grid-template-columns: max-content max-content !important;
    column-gap: 8px !important;
    width: max-content !important;
    max-width: 100% !important;
  }
}
#invoice-pdf .status100-mobile-items {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
#invoice-pdf .status100-mobile-item-head {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
#invoice-pdf .status100-mobile-item-head-main {
  flex: 1 1 0%;
  min-width: 0;
}
#invoice-pdf .status100-mobile-item-fields {
  margin-top: 12px;
}
#invoice-pdf .rdt-mobile .status100-mobile-item-head > div:first-child {
  flex-shrink: 0;
  min-width: 80px;
}
#invoice-pdf .rdt-mobile .status100-mobile-item-head img,
#invoice-pdf .rdt-mobile .invoice-status100-thumb img {
  max-width: none;
  width: 80px;
  height: 80px;
  object-fit: contain;
}
#invoice-pdf .rdt-mobile .status100-mobile-item-fields > .po-field:not(:first-child) {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
  padding: 8px 0;
}
#invoice-pdf .rdt-mobile .status100-mobile-item-fields > .po-field:not(:first-child) .po-field-label {
  margin-bottom: 0;
}
#invoice-pdf .rdt-mobile .status100-mobile-item-fields > .po-field:not(:first-child) .po-field-value {
  text-align: right;
  font-weight: 500;
}
#invoice-pdf .rdt-mobile .status100-mobile-item-fields > .po-field:not(:first-child) .po-field-value.status100-total-bold {
  font-weight: 600;
}
@media screen and (max-width: 900px) {
  #invoice-pdf .status100-hero.status100-pad {
    padding-bottom: 20px;
    border-bottom: 1px solid #e5e7eb;
  }
  #invoice-pdf .status100-hero {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto auto;
    column-gap: 0;
    row-gap: 10px;
  }
  #invoice-pdf .status100-hero-title-wrap {
    grid-column: 1;
    grid-row: 1;
    justify-self: center;
    text-align: center;
    width: 100%;
    max-width: 100%;
  }
  #invoice-pdf .status100-hero .status100-brand {
    grid-column: 1;
    grid-row: 2;
    justify-self: start;
    align-items: flex-start;
    width: auto;
  }
  #invoice-pdf .status100-hero .status100-brand img {
    margin-left: 0;
    margin-right: 0;
    max-width: 150px;
    max-height: 48px;
  }
  #invoice-pdf .status100-hero .status100-header-right {
    grid-column: 1;
    grid-row: 3;
    justify-self: end;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    align-items: flex-end;
    text-align: right;
  }
  #invoice-pdf .status100-hero .status100-title {
    font-size: 18px;
    line-height: 1.2;
  }
  #invoice-pdf .status100-customer-grid,
  #invoice-pdf .status100-address-grid {
    grid-template-columns: 1fr;
  }
  #invoice-pdf .status100-section-head {
    justify-content: flex-start;
  }
}
@media screen and (max-width: 640px) {
  #invoice-pdf .status100-pad,
  #invoice-pdf .status100-footer {
    padding: 20px 16px;
  }
  #invoice-pdf .status100-hero .status100-title {
    font-size: 16px;
  }
  #invoice-pdf .status100-hero .status100-brand img {
    max-width: 120px;
    max-height: 40px;
  }
}
@media screen and (min-width: 1022px) {
  #invoice-pdf .rdt-desktop {
    display: block !important;
  }
  #invoice-pdf .rdt-mobile {
    display: none !important;
  }
}
#invoice-pdf .status100-table-wrap {
  overflow: visible !important;
  overflow-x: visible !important;
  overflow-y: visible !important;
  width: 100% !important;
}

#invoice-pdf .status100-table,
.invoice-root .status100-table,
#invoice-pdf .invoice-pdf-items-table,
.invoice-root .invoice-pdf-items-table {
  width: 100% !important;
  min-width: 0 !important;
  table-layout: fixed !important;
  border-collapse: separate !important;
  border-spacing: 0 !important;
}

#invoice-pdf .status100-table thead th,
.invoice-root .status100-table thead th {
  vertical-align: middle !important;
  text-align: left !important;
  white-space: nowrap !important;
  padding: 12px 10px !important;
}

#invoice-pdf .invoice-pdf-items-table thead th,
.invoice-root .invoice-pdf-items-table thead th {
  white-space: normal !important;
  line-height: 1.15 !important;
  font-size: 0.78rem !important;
}

#invoice-pdf .status100-table tbody td,
.invoice-root .status100-table tbody td {
  vertical-align: top !important;
  text-align: left !important;
  padding: 12px 10px !important;
  line-height: 1.45 !important;
}

#invoice-pdf .invoice-pdf-col-image,
.invoice-root .invoice-pdf-col-image {
  width: 10% !important;
  text-align: left !important;
  vertical-align: top !important;
  padding-left: 0 !important;
  padding-right: 10px !important;
}

#invoice-pdf .invoice-pdf-image-cell-wrap,
.invoice-root .invoice-pdf-image-cell-wrap {
  display: flex !important;
  align-items: flex-start !important;
  justify-content: flex-start !important;
  width: 100% !important;
}

#invoice-pdf .invoice-pdf-col-product,
.invoice-root .invoice-pdf-col-product {
  width: 18% !important;
  text-align: left !important;
  padding-left: 14px !important;
}

#invoice-pdf .invoice-pdf-col-details,
.invoice-root .invoice-pdf-col-details {
  width: 14% !important;
  text-align: left !important;
}

#invoice-pdf .invoice-pdf-col-qty,
.invoice-root .invoice-pdf-col-qty {
  width: 6% !important;
  text-align: center !important;
  vertical-align: top !important;
}

#invoice-pdf .invoice-pdf-col-unit,
.invoice-root .invoice-pdf-col-unit {
  width: 9% !important;
  text-align: right !important;
  vertical-align: top !important;
  white-space: nowrap !important;
}

#invoice-pdf .invoice-pdf-col-changed,
.invoice-root .invoice-pdf-col-changed {
  width: 9% !important;
  text-align: right !important;
  vertical-align: top !important;
  white-space: nowrap !important;
}

#invoice-pdf .invoice-pdf-col-discount,
.invoice-root .invoice-pdf-col-discount {
  width: 9% !important;
  text-align: center !important;
  vertical-align: middle !important;
}

#invoice-pdf .invoice-pdf-col-tax,
.invoice-root .invoice-pdf-col-tax {
  width: 8% !important;
  text-align: right !important;
  vertical-align: top !important;
  white-space: nowrap !important;
  display: table-cell !important;
  visibility: visible !important;
  opacity: 1 !important;
}

#invoice-pdf .invoice-pdf-col-total,
.invoice-root .invoice-pdf-col-total {
  width: 10% !important;
  text-align: right !important;
  vertical-align: top !important;
  white-space: nowrap !important;
  display: table-cell !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.invoice-root .status100-meta-grid {
  grid-template-columns: 98px minmax(110px, 1fr) !important;
  column-gap: 6px !important;
  row-gap: 8px !important;
  align-items: center !important;
}
.invoice-root .status100-meta-grid .status100-meta-label,
.invoice-root .status100-meta-grid .status100-meta-value {
  display: block !important;
  line-height: 1.35 !important;
}
.invoice-root .status100-meta-grid .status100-meta-label {
  text-align: left !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
}
.invoice-root .invoice-pdf-meta-label-word-second { padding-left: 4px !important; }
.invoice-root .status100-meta-grid .status100-meta-value {
  text-align: right !important;
  font-weight: 500 !important;
}
.invoice-root .invoice-pdf-col-image .status100-product-cell { justify-content: flex-start !important; }
.invoice-root .invoice-pdf-col-image .status100-thumb,
.invoice-root .invoice-pdf-col-image .status100-thumb-fallback {
  margin-left: 0 !important;
  margin-right: auto !important;
}
#invoice-pdf .invoice-pdf-col-product .status100-product-name,
.invoice-root .invoice-pdf-col-product .status100-product-name {
  display: block !important;
  margin: 0 !important;
  line-height: 1.4 !important;
  word-break: break-word !important;
  overflow-wrap: anywhere !important;
}

#invoice-pdf .invoice-pdf-col-product .status100-product-sku,
.invoice-root .invoice-pdf-col-product .status100-product-sku,
#invoice-pdf .invoice-pdf-col-details .meta-value,
.invoice-root .invoice-pdf-col-details .meta-value {
  display: block !important;
  margin-top: 4px !important;
  line-height: 1.35 !important;
  word-break: break-word !important;
  overflow-wrap: anywhere !important;
}

#invoice-pdf .invoice-pdf-3col-table,
.invoice-root .invoice-pdf-3col-table {
  width: 100% !important;
  table-layout: fixed !important;
  border-collapse: collapse !important;
  border-spacing: 0 !important;
}

#invoice-pdf .invoice-pdf-3col-table th,
#invoice-pdf .invoice-pdf-3col-table td,
.invoice-root .invoice-pdf-3col-table th,
.invoice-root .invoice-pdf-3col-table td {
  box-sizing: border-box !important;
  padding: 14px 12px !important;
  text-align: center !important;
  vertical-align: middle !important;
  white-space: normal !important;
  word-break: break-word !important;
  overflow-wrap: anywhere !important;
}

#invoice-pdf .invoice-pdf-3col-table thead th,
.invoice-root .invoice-pdf-3col-table thead th {
  font-weight: 700 !important;
  color: #111827 !important;
  background: #f3f4f6 !important;
  border-bottom: 1px solid #e5e7eb !important;
}

#invoice-pdf .invoice-pdf-3col-table tbody td,
.invoice-root .invoice-pdf-3col-table tbody td {
  border-bottom: 1px solid #e5e7eb !important;
}

#invoice-pdf .invoice-pdf-3col-table .invoice-pdf-col-image,
#invoice-pdf .invoice-pdf-3col-table .invoice-pdf-col-product,
#invoice-pdf .invoice-pdf-3col-table .invoice-pdf-col-details,
#invoice-pdf .invoice-pdf-3col-table .invoice-pdf-col-qty,
.invoice-root .invoice-pdf-3col-table .invoice-pdf-col-image,
.invoice-root .invoice-pdf-3col-table .invoice-pdf-col-product,
.invoice-root .invoice-pdf-3col-table .invoice-pdf-col-details,
.invoice-root .invoice-pdf-3col-table .invoice-pdf-col-qty {
  text-align: center !important;
  padding-left: 12px !important;
  padding-right: 12px !important;
}
#invoice-pdf .invoice-pdf-3col-table .invoice-pdf-col-image,
.invoice-root .invoice-pdf-3col-table .invoice-pdf-col-image { width: 18% !important; }
#invoice-pdf .invoice-pdf-3col-table .invoice-pdf-col-product,
.invoice-root .invoice-pdf-3col-table .invoice-pdf-col-product { width: 44% !important; }
#invoice-pdf .invoice-pdf-3col-table .invoice-pdf-col-details,
.invoice-root .invoice-pdf-3col-table .invoice-pdf-col-details { width: 22% !important; }
#invoice-pdf .invoice-pdf-3col-table .invoice-pdf-col-qty,
.invoice-root .invoice-pdf-3col-table .invoice-pdf-col-qty { width: 16% !important; }

#invoice-pdf .invoice-pdf-3col-table .invoice-cell-center,
.invoice-root .invoice-pdf-3col-table .invoice-cell-center {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  min-height: 48px !important;
  text-align: center !important;
}

/* Installation / removal line items: thumbnail + title (matches Order Summary layout). */
#invoice-pdf .invoice-pdf-3col-table .invoice-job-line-item-details,
.invoice-root .invoice-pdf-3col-table .invoice-job-line-item-details {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  gap: 12px !important;
  width: 100% !important;
  min-height: 48px !important;
  text-align: left !important;
}
#invoice-pdf .invoice-pdf-3col-table .invoice-job-line-item-details .status100-product-name,
.invoice-root .invoice-pdf-3col-table .invoice-job-line-item-details .status100-product-name {
  flex: 1 1 auto !important;
  min-width: 0 !important;
  text-align: left !important;
}

#invoice-pdf .invoice-pdf-3col-table .status100-product-name,
#invoice-pdf .invoice-pdf-3col-table .meta-value,
.invoice-root .invoice-pdf-3col-table .status100-product-name,
.invoice-root .invoice-pdf-3col-table .meta-value {
  display: block !important;
  width: 100% !important;
  margin: 0 !important;
  text-align: center !important;
  line-height: 1.4 !important;
  word-break: break-word !important;
  overflow-wrap: anywhere !important;
}

#invoice-pdf .status100-table tr.invoice-item-row > td,
.invoice-root .status100-table tr.invoice-item-row > td {
  border-bottom: 1px solid #e5e7eb !important;
}

#invoice-pdf tr.invoice-item-row {
  break-inside: avoid !important;
  page-break-inside: avoid !important;
}

#invoice-pdf tr.invoice-item-row > td {
  break-inside: avoid !important;
  page-break-inside: avoid !important;
}

#invoice-pdf .invoice-kv-row,
#invoice-pdf .status100-total-line,
#invoice-pdf .status100-payment-line,
#invoice-pdf .invoice-footer-line {
  break-inside: avoid !important;
  page-break-inside: avoid !important;
}

#invoice-pdf .invoice-notes-box-full,
#invoice-pdf .status100-payment-card,
#invoice-pdf .invoice-footer-terms-block,
#invoice-pdf .invoice-footer-powered {
  break-inside: auto !important;
  page-break-inside: auto !important;
}

/* Bottom: full-width notes, then payment | order total side by side */
.invoice-bottom-stack {
  width: 100%;
  margin-top: 8px;
  box-sizing: border-box;
}
.invoice-notes-full {
  width: 100%;
  margin-bottom: 20px;
}
.invoice-notes-box-full {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box;
  break-inside: auto !important;
  page-break-inside: auto !important;
}
.invoice-payment-totals-row {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: stretch;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
  break-inside: auto !important;
  page-break-inside: auto !important;
}
.invoice-payment-totals-row .status100-payment-card {
  flex: 1 1 0;
  min-width: 0;
  width: auto !important;
  max-width: none !important;
  margin-left: 0 !important;
  break-inside: auto !important;
  page-break-inside: auto !important;
}
.invoice-tx-wrap {
  text-align: right;
  max-width: 58%;
  word-break: break-all;
  font-weight: 700 !important;
}
.invoice-amount-paid-box,
.invoice-grand-total-box {
  margin-top: 12px;
}
#invoice-pdf .invoice-order-half .status100-bottom-pay-block,
.invoice-order-half .status100-bottom-pay-block {
  width: 100%;
  box-sizing: border-box;
}
#invoice-pdf .invoice-order-half .status100-bottom-pay-block .status100-pay-btn {
  margin-top: 0;
}
@media screen and (max-width: 1021px) {
  #invoice-pdf .invoice-payment-totals-row {
    flex-direction: column;
    flex-wrap: wrap;
    align-items: stretch;
    gap: 16px;
  }
  #invoice-pdf .invoice-payment-totals-row .status100-payment-card {
    flex: 1 1 auto !important;
    width: 100% !important;
    max-width: 100% !important;
  }
}

/* Footer: aligned contact grid, then terms, then powered by */
.invoice-footer-panel {
  margin-top: 0;
  border-top: 1px solid #e5e7eb;
  background: #fafbfd;
  padding: 24px 32px 26px;
  box-sizing: border-box;
  break-inside: avoid-page !important;
  page-break-inside: avoid !important;
}
.invoice-footer-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px 28px;
  margin-bottom: 16px;
  break-inside: avoid-page !important;
  page-break-inside: avoid !important;
}
.invoice-footer-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.invoice-footer-field-address {
  grid-column: span 2;
}
.invoice-footer-k {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-family: Roboto, sans-serif;
}
.invoice-footer-v {
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
  line-height: 1.55;
  word-break: break-word;
  overflow-wrap: anywhere;
  font-family: Roboto, sans-serif;
}
.invoice-footer-website .status100-footer-link {
  word-break: break-all;
}
.invoice-footer-terms-block {
  margin-top: 12px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
  break-inside: avoid-page !important;
  page-break-inside: avoid !important;
}
.invoice-footer-terms-title {
  font-weight: 700;
  font-size: 0.975rem;
  color: #111827;
  margin-bottom: 10px;
  font-family: Roboto, sans-serif;
}
.invoice-footer-terms-body {
  font-size: 0.875rem;
  color: #374151;
  line-height: 1.75;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  font-family: Roboto, sans-serif;
  font-weight: 500;
}
.invoice-footer-powered {
  text-align: center;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #e5e7eb;
  font-size: 12px;
  font-weight: 500;
  color: #4b5563;
  word-break: break-word;
  overflow-wrap: anywhere;
  break-inside: avoid-page !important;
  page-break-inside: avoid !important;
}
@media (max-width: 640px) {
  .invoice-footer-grid {
    grid-template-columns: 1fr;
  }
  .invoice-footer-field-address {
    grid-column: auto;
  }
  .invoice-footer-panel {
    padding: 20px 16px 22px;
  }
}

/* Address section - ensure full width on all devices */
.address-section .row.m-0 { 
  margin-left: 0 !important; 
  margin-right: 0 !important; 
  width: 100% !important;
}
.address-section .address-col {
  padding-left: 0 !important;
  padding-right: 0 !important;
  width: 100% !important;
}
@media (min-width: 992px) {
  .address-section .address-col {
    width: 50% !important;
    flex: 0 0 50% !important;
    max-width: 50% !important;
    padding-right: 1rem !important;
  }
  .address-section .address-col:first-child {
    padding-right: 0.5rem !important;
    padding-left: 0 !important;
  }
  .address-section .address-col:last-child {
    padding-left: 0.5rem !important;
    padding-right: 0 !important;
  }
}
/* Payment Details and Order Total - add gap between cards, side by side */
.payment-totals-row {
  margin-top: 1.5rem !important;
}
@media (min-width: 768px) {
  .payment-details-col {
    flex: 0 0 50% !important;
    max-width: 50% !important;
    width: 50% !important;
    padding-right: 1rem !important;
  }
  .order-total-col {
    flex: 0 0 50% !important;
    max-width: 50% !important;
    width: 50% !important;
    padding-left: 1rem !important;
  }
}
@media (max-width: 767.98px) {
  .payment-details-col,
  .order-total-col {
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-bottom: 1rem;
  }
}
.rounded-4 { border-radius: 12px; }
.brand-logo { width: 80px; height: 80px; object-fit: contain; border-radius: 8px; }
.invoice-title { font-weight: 700; font-size: 24px; color: #212529; }
.soft-hr { margin: 1rem 0 1.25rem; opacity: .3; }
.status-pill { padding: .4rem .75rem; font-size: 12px; font-weight: 600; }
.status-blue { background: #EEF5FF; color: #155dfc; border: 1px solid #E6EEFF; }
.status-yellow { background: #FFF6DB; color: #9A6B00; border: 1px solid #FBEFC4; }
.meta-label { color: #364153; font-size: 16px; font-weight: 500; }
.meta-value { color: #364153; font-size: 15px; font-weight: 400; }

/* Cards look closer to your reference */
.soft-panel,
.card.bg-gray-50 {
  background: #ffffffe6;
  border: 1px solid #dee2e6;
  border-radius: 8px;
}
.card .card-body { padding: 16px 20px; }

/* Table look & density */
.invoice-table { table-layout: fixed; }
.invoice-table th {
  word-break: break-word; 
  padding: 12px 14px; 
  font-size: 13px; 
  font-weight: 600; 
  color: #495057; 
  background-color: #f8f9fa;
}
.invoice-table td { 
  word-break: break-word; 
  padding: 12px 14px; 
  font-size: 14px; 
  color: #212529;
  border-top: 1px solid #dee2e6; 
}

.img-box { width:54px; height:54px; border:1px solid #dee2e6; border-radius:8px; overflow:hidden; display:flex; align-items:center; justify-content:center; background:#fff; }
.mini-thumb { width:42px; height:42px; object-fit:contain; border-radius:6px; }
.grand-box { background:#fff; border:1px solid #dee2e6; border-radius:8px; padding:14px 18px; }
.no-img { font-size: 11px; color:#6c757d; }
.priority-pill { padding: .3rem .65rem; border-radius: 20px; font-weight: 600; font-size: 11px; border: 1px solid #ffd5d8; background: #ffe8ea; color: #b91c1c; }
.priority-pill.medium { background: #fff6db; color: #9A6B00; border-color: #fbeec4; }
.priority-pill.low { background: #e7f5ff; color: #155dfc; border-color: #d9ebff; }
.section-h6 { font-weight: 600; font-size: 16px; color: #212529; }
.bg-gray-50 { background-color: #ffffffe6; border-color: #dee2e6; border-radius: 8px; }
.address { line-height: 1.6; font-size: 14px; color: #6c757d; }
.address-name { font-size: 15px; font-weight: 600; color: #212529; }
.address-phone { font-size: 14px; color: #6c757d; }

/* Additional typography improvements */
.invoice-root h1, .invoice-root h2, .invoice-root h3, .invoice-root h4, .invoice-root h5, .invoice-root h6 {
  color: #212529;
  font-weight: 600;
}
.invoice-root .text-muted { color: #6c757d !important; }
.invoice-root .text-primary { color: #0d6efd !important; }
.invoice-root .text-danger { color: #dc3545 !important; }
.invoice-root .fw-semibold { font-weight: 600; }
.invoice-root .fw-medium { font-weight: 500; }
.invoice-root .small { font-size: 13px; }
.invoice-root .card { border-radius: 8px; border: 1px solid f1ecece6; }
.invoice-root .rounded-lg { border-radius: 8px !important; }

/* Totals and payment sections */
.invoice-root .grand-box strong { font-size: 15px; font-weight: 600; color: #212529; }
.invoice-root .grand-box .text-primary { font-size: 16px; font-weight: 600; color: #0d6efd !important; }
.invoice-root .card-body .d-flex.justify-content-between span { font-size: 14px; }
.invoice-root .card-body .d-flex.justify-content-between .text-muted { font-size: 14px; color: #6c757d !important; }
.invoice-root .card-body .d-flex.justify-content-between .fw-semibold { font-size: 14px; font-weight: 600; color: #212529; }
.invoice-root hr { border-color: #dee2e6; opacity: 1; }

/* Export-safe typography */
.pdf-export, .pdf-export * {
  font-variant-ligatures: none !important;
  font-feature-settings: "liga" 0, "clig" 0, "calt" 0, "kern" 0 !important;
  font-kerning: none !important;
  -webkit-font-smoothing: antialiased !important;
  -moz-osx-font-smoothing: grayscale !important;
  text-rendering: geometricPrecision !important;
  letter-spacing: 0.01em !important;
  // line-height: 1.5 !important;
}

/* Base font sizes */
.pdf-export { font-size: 14px; color: #212529; }
.pdf-export h1 { font-size: 28px; font-weight: 700; color: #212529; }
.pdf-export h2 { font-size: 24px; font-weight: 700; color: #212529; }
.pdf-export h3 { font-size: 20px; font-weight: 600; color: #212529; }
.pdf-export h4 { font-size: 18px; font-weight: 600; color: #212529; }
.pdf-export h5 { font-size: 16px; font-weight: 600; color: #212529; }
.pdf-export h6 { font-size: 16px; font-weight: 600; color: #212529; }
.pdf-export p { font-size: 14px; color: #212529; line-height: 1.6; }
.pdf-export a { color: #0d6efd; text-decoration: none; }
.pdf-export a:hover { color: #0a58ca; text-decoration: underline; }

/* Hide print-only footer on screen */
.print-page-footer { display: none !important; }

/* ==== Print (desktop layout, clean breaks, no blank pages) ==== */
/* Bottom margin leaves room for page number (same zone as browser footer) */
@page {
  size: A4 portrait;
  margin: 10mm 10mm 20mm 10mm;
  /* Page number in margin box: "1 of 3", "2 of 3", ... */
  @bottom-right {
    content: counter(page) " of " counter(pages);
    font-size: 14px;
    color: #1f2937;
  }
}

@media print {
  /* Print ONLY the invoice (hide app shell) */
  body * { visibility: hidden !important; }
  #invoice-pdf, #invoice-pdf * { visibility: visible !important; }

  /* Hide buttons and no-print elements when printing */
  .no-print,
  .d-print-none { display: none !important; }

  /* Start at top-left, A4 width */
  #invoice-pdf {
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
    width: 794px !important;
    max-width: 794px !important;
    margin: 0 auto !important;
  }
  .invoice-root {
    max-width: 794px !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }
  .container { max-width: 794px !important; padding-left: 0 !important; padding-right: 0 !important; }

  /* Force desktop grid widths */
  .row { display:flex !important; flex-wrap:wrap !important; width:100% !important; margin-left: 0 !important; margin-right: 0 !important; }
  .col-12 { width:100% !important; padding-left: 0 !important; padding-right: 0 !important; }
  /* Force col-md-6 to be side by side in print (overrides col-12) */
  .col-md-6 { width:50% !important; flex: 0 0 50% !important; max-width: 50% !important; }
  .col-lg-6 { width:50% !important; flex: 0 0 50% !important; max-width: 50% !important; }
  .col-lg-5 { width:41.6667% !important; }
  .col-lg-4 { width:33.3333% !important; }
  .col-lg-3 { width:25% !important; }
  /* Address section spacing - ensure proper width */
  .address-section .row { margin-left: 0 !important; margin-right: 0 !important; width: 100% !important; }
  .address-section .address-col { padding-left: 0 !important; padding-right: 0 !important; width: 100% !important; }
  .address-section .address-col:first-child { padding-right: 0.5rem !important; }
  .address-section .address-col:last-child { padding-left: 0.5rem !important; padding-right: 0 !important; width: calc(50% - 0.5rem) !important; }
  /* Payment Details and Order Total - add gap in print, ensure side by side */
  .payment-totals-row { margin-top: 1.5rem !important; }
  .payment-details-col {
    flex: 0 0 50% !important;
    max-width: 50% !important;
    width: 50% !important;
    padding-right: 1rem !important;
  }
  .order-total-col {
    flex: 0 0 50% !important;
    max-width: 50% !important;
    width: 50% !important;
    padding-left: 1rem !important;
  }

  .invoice-payment-totals-row {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    align-items: stretch !important;
    gap: 12px !important;
    width: 100% !important;
  }
  .invoice-payment-totals-row .status100-payment-card {
    flex: 1 1 0 !important;
    min-width: 0 !important;
    width: auto !important;
    max-width: none !important;
    margin-left: 0 !important;
  }
  .invoice-notes-box-full {
    max-width: 100% !important;
    width: 100% !important;
  }
  .invoice-footer-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }

  .table { break-inside: auto; page-break-inside: auto; }
  .table thead { display: table-header-group !important; }
  .table tfoot { display: table-footer-group !important; }

  tr.invoice-item-row,
  tr.invoice-item-row > td,
  .invoice-kv-row,
  .status100-total-line,
  .status100-payment-line,
  .invoice-footer-line {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  .invoice-notes-box-full,
  .status100-payment-card,
  .invoice-footer-terms-block,
  .invoice-footer-powered,
  .invoice-payment-totals-row,
  .invoice-footer-panel {
    break-inside: auto !important;
    page-break-inside: auto !important;
  }

  /* Invoice tables: repeat header on each printed page */
  .invoice-root table thead { display: table-header-group !important; }
  thead { display: table-header-group; }
  tfoot { display: table-footer-group; }

  /* Only protect *small critical blocks* from splitting */
  // .avoid-break,
  // .grand-box { break-inside: avoid; page-break-inside: avoid; }

  /* Optional page-break helpers */
  .page-break-before { page-break-before: always !important; }
  .page-break-after { page-break-after: always !important; }

  /* Page numbers in bottom-right: "1 of 3", "2 of 3", ... */
  .print-page-footer { display: none !important; }

  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
`;


export default InvoiceDocument;
